// Static interface copy in every supported app language. User and model content is not translated here.
import type { NamespaceCatalog } from '@/i18n/ns';

const en = {
  title: 'Coding agents',
  intro:
    'The coding agents you run yourself can read your company’s knowledge and work with apps. Each one asks for exactly the access it needs, and nothing grows without your say-so.',
  none: 'No coding agents are connected yet.',
  loading: 'Loading coding agents…',
  error: 'We couldn’t load your coding agents. Try again.',
  consentTitle: 'Allow an AI agent to use {company}?',
  notVerified:
    'An AI agent that calls itself “{client}” wants to use {company}. Poneva can’t verify the name.',
  notVerifiedOrigin:
    'An AI agent that calls itself “{client}” on {origin} wants to use {company}. Poneva can’t verify the name.',
  dataLine:
    'What it reads goes to {vendor} under {person}’s own plan, outside Poneva’s EU hosting.',
  expiryDays:
    'Access lasts {days} days. You can revoke it any time in Connections → Coding agents.',
  expiryDay: 'Access lasts {days} day. You can revoke it any time in Connections → Coding agents.',
  headingAsking: 'It asks to',
  headingRefused: 'This request was refused',
  headingConsented: 'You allowed',
  headingRevoked: 'This access was revoked',
  headingStale: 'This request is out of date',
  availAsked: 'Will be allowed',
  availAlready: 'Already allowed',
  availNotAvailable: 'Not available to you',
  notAvailableNote: 'Your role at {company} doesn’t include this, so it won’t be granted.',
  notePartly:
    'Some of what it asked for isn’t available to you. If you allow it, only the access marked “Will be allowed” is granted.',
  noteBeyond:
    'None of what it asked for is available to you, so there is nothing to allow. Ask a company admin.',
  noteStale:
    'Your company access changed after the agent asked. Ask the agent to connect again so you review the current request.',
  noteRevoked: 'The agent can no longer use this access.',
  noteConsented: 'You can change or revoke this at any time under Coding agents.',
  refusalReadRequired:
    'An agent can’t ask for app access alone. It also needs to read company knowledge. Ask the agent to connect again with both.',
  refusalGeneral: 'The request was refused. Ask the agent to connect again.',
  allow: 'Allow',
  deny: 'Cancel',
  working: 'Working…',
  unconfirmed:
    'We could not confirm your choice. It may or may not have been recorded. Check again before choosing once more.',
  check: 'Check again',
  stillPending:
    'Poneva has not settled this yet. It may still be going through, so nothing else can be decided until it is.',
  stillUnknown:
    'We could not get an answer. Nothing else can be decided until this is settled. Try again in a moment.',
  requestClosed: 'This request is closed. Ask the agent to connect again to start a new one.',
  recordedOther: 'Another choice was already recorded: {choice}. Your choice was not applied.',
  choiceAllow: 'allowed',
  choiceDeny: 'not allowed',
  staleOnSend:
    'Your access changed since this request was made. Nothing was granted. Ask the agent to connect again.',
  refusedOnSend: 'Poneva refused this request. Nothing was granted.',
  noAccess: 'No access',
  lastSeenNever: 'Not used yet',
  lastSeenStale: 'Not seen recently · last seen {when}',
  lastSeen: 'Last seen {when}',
  stateActive: 'Connected',
  stateRevoked: 'Revoked',
  stateExpired: 'Expired',
  needsConsent: 'Asking for more access',
  review: 'Review request',
  revoke: 'Revoke',
  revokeConfirm: 'Revoke {client}? It stops working at once. You can connect it again later.',
  revokeYes: 'Revoke',
  revokeNo: 'Keep',
  revokeFailed: 'We couldn’t revoke it. It may still be connected. Try again.',
  revokeDenied: 'You can’t revoke this agent.',
  revokeGone: 'This agent is already gone. The list shows the current state.',
  yourAgents: 'Your agents',
  companyAgents: 'Everyone’s agents',
  companyAgentsHelp: 'As a company admin you can see and revoke every coding agent.',
};

export type CodingAgentsCopyKey = keyof typeof en;
export type CodingAgentsCopy = Readonly<Record<CodingAgentsCopyKey, string>>;

const copy: NamespaceCatalog<keyof typeof en> = {
  bg: {
    title: 'Агенти за програмиране',
    intro:
      'Агентите за програмиране, които стартирате сами, могат да четат фирмените ви знания и да работят с приложения. Всеки от тях иска точно необходимия му достъп и нищо повече не се добавя без вашето одобрение.',
    none: 'Все още няма свързани агенти за програмиране.',
    loading: 'Зареждане на агентите за програмиране…',
    error: 'Не успяхме да заредим агентите ви за програмиране. Опитайте отново.',
    consentTitle: 'Да разрешите ли на AI агент да използва {company}?',
    notVerified:
      'AI агент, който се нарича „{client}“, иска да използва {company}. Poneva не може да потвърди името.',
    notVerifiedOrigin:
      'AI агент, който се нарича „{client}“ в {origin}, иска да използва {company}. Poneva не може да потвърди името.',
    dataLine:
      'Прочетените от него данни се изпращат до {vendor} по личния план на {person}, извън хостинга на Poneva в ЕС.',
    expiryDays:
      'Достъпът е валиден {days} дни. Можете да го отмените по всяко време в „Връзки“ → „Агенти за програмиране“.',
    expiryDay:
      'Достъпът е валиден {days} ден. Можете да го отмените по всяко време в „Връзки“ → „Агенти за програмиране“.',
    headingAsking: 'Иска',
    headingRefused: 'Тази заявка беше отказана',
    headingConsented: 'Разрешихте',
    headingRevoked: 'Този достъп беше отменен',
    headingStale: 'Тази заявка е остаряла',
    availAsked: 'Ще бъде разрешено',
    availAlready: 'Вече е разрешено',
    availNotAvailable: 'Не е налично за вас',
    notAvailableNote: 'Ролята ви в {company} не включва това, затова няма да бъде предоставено.',
    notePartly:
      'Част от поисканото не е налично за вас. Ако разрешите, ще бъде предоставен само достъпът, отбелязан с „Ще бъде разрешено“.',
    noteBeyond:
      'Нищо от поисканото не е налично за вас, затова няма какво да разрешите. Обърнете се към администратор на компанията.',
    noteStale:
      'Достъпът ви до компанията се промени, след като агентът изпрати заявката. Помолете агента да се свърже отново, за да прегледате текущата заявка.',
    noteRevoked: 'Агентът вече не може да използва този достъп.',
    noteConsented:
      'Можете да промените или отмените това по всяко време в „Агенти за програмиране“.',
    refusalReadRequired:
      'Агентът не може да поиска само достъп до приложения. Необходимо му е и да чете фирмените знания. Помолете агента да се свърже отново и да поиска и двете.',
    refusalGeneral: 'Заявката беше отказана. Помолете агента да се свърже отново.',
    allow: 'Разреши',
    deny: 'Отказ',
    working: 'Изпълнение…',
    unconfirmed:
      'Не успяхме да потвърдим избора ви. Той може да е записан, а може и да не е. Проверете отново, преди да изберете още веднъж.',
    check: 'Провери отново',
    stillPending:
      'Poneva все още не е приключила обработката на това. Възможно е да се обработва, затова не може да се вземе друго решение, докато не приключи.',
    stillUnknown:
      'Не успяхме да получим отговор. Не може да се вземе друго решение, докато това не бъде уредено. Опитайте отново след малко.',
    requestClosed:
      'Тази заявка е затворена. Помолете агента да се свърже отново, за да започне нова.',
    recordedOther: 'Вече е записан друг избор: {choice}. Вашият избор не беше приложен.',
    choiceAllow: 'разрешен',
    choiceDeny: 'не е разрешен',
    staleOnSend:
      'Достъпът ви се промени, след като тази заявка беше създадена. Нищо не беше предоставено. Помолете агента да се свърже отново.',
    refusedOnSend: 'Poneva отказа тази заявка. Нищо не беше предоставено.',
    noAccess: 'Няма достъп',
    lastSeenNever: 'Все още не е използван',
    lastSeenStale: 'Не е виждан наскоро · последно видян {when}',
    lastSeen: 'Последно видян {when}',
    stateActive: 'Свързан',
    stateRevoked: 'Отменен',
    stateExpired: 'Изтекъл',
    needsConsent: 'Иска допълнителен достъп',
    review: 'Прегледай заявката',
    revoke: 'Отмени',
    revokeConfirm:
      'Да отмените ли {client}? Той ще спре да работи незабавно. Можете да го свържете отново по-късно.',
    revokeYes: 'Отмени достъпа',
    revokeNo: 'Запази',
    revokeFailed:
      'Не успяхме да отменим достъпа. Възможно е агентът все още да е свързан. Опитайте отново.',
    revokeDenied: 'Не можете да отмените достъпа на този агент.',
    revokeGone: 'Този агент вече не съществува. Списъкът показва текущото състояние.',
    yourAgents: 'Вашите агенти',
    companyAgents: 'Агентите на всички',
    companyAgentsHelp:
      'Като администратор на компанията можете да виждате и да отменяте достъпа на всеки програмен агент.',
  },
  hr: {
    title: 'Programerski agenti',
    intro:
      'Programerski agenti koje sami pokrećete mogu čitati znanje vaše tvrtke i raditi s aplikacijama. Svaki traži točno onaj pristup koji mu je potreban i ništa se ne proširuje bez vašeg odobrenja.',
    none: 'Još nema povezanih programerskih agenata.',
    loading: 'Učitavanje programerskih agenata…',
    error: 'Nismo mogli učitati vaše programerske agente. Pokušajte ponovno.',
    consentTitle: 'Dopustiti AI agentu upotrebu tvrtke {company}?',
    notVerified:
      'AI agent koji se predstavlja kao „{client}” želi upotrebljavati tvrtku {company}. Poneva ne može potvrditi taj naziv.',
    notVerifiedOrigin:
      'AI agent koji se na adresi {origin} predstavlja kao „{client}” želi upotrebljavati tvrtku {company}. Poneva ne može potvrditi taj naziv.',
    dataLine:
      'Ono što pročita šalje se pružatelju {vendor} u okviru vlastitog plana osobe {person}, izvan Ponevina hostinga u EU-u.',
    expiryDays:
      'Pristup traje {days} dana. Možete ga opozvati u bilo kojem trenutku u odjeljku Veze → Programerski agenti.',
    expiryDay:
      'Pristup traje {days} dan. Možete ga opozvati u bilo kojem trenutku u odjeljku Veze → Programerski agenti.',
    headingAsking: 'Traži mogućnost',
    headingRefused: 'Ovaj je zahtjev odbijen',
    headingConsented: 'Dopustili ste',
    headingRevoked: 'Ovaj je pristup opozvan',
    headingStale: 'Ovaj zahtjev više nije aktualan',
    availAsked: 'Bit će dopušteno',
    availAlready: 'Već dopušteno',
    availNotAvailable: 'Nije vam dostupno',
    notAvailableNote:
      'Vaša uloga u tvrtki {company} ne uključuje ovo, pa pristup neće biti odobren.',
    notePartly:
      'Nešto od onoga što je zatraženo nije vam dostupno. Ako dopustite pristup, odobrit će se samo pristup označen s „Bit će dopušteno”.',
    noteBeyond:
      'Ništa od onoga što je zatraženo nije vam dostupno, pa nema ničega što možete dopustiti. Obratite se administratoru tvrtke.',
    noteStale:
      'Vaš se pristup tvrtki promijenio nakon zahtjeva agenta. Zatražite od agenta da se ponovno poveže kako biste pregledali trenutačni zahtjev.',
    noteRevoked: 'Agent više ne može upotrebljavati ovaj pristup.',
    noteConsented:
      'Ovo možete promijeniti ili opozvati u bilo kojem trenutku u odjeljku Programerski agenti.',
    refusalReadRequired:
      'Agent ne može zatražiti samo pristup aplikacijama. Mora moći i čitati znanje tvrtke. Zatražite od agenta da se ponovno poveže s obje vrste pristupa.',
    refusalGeneral: 'Zahtjev je odbijen. Zatražite od agenta da se ponovno poveže.',
    allow: 'Dopusti',
    deny: 'Odustani',
    working: 'Obrada…',
    unconfirmed:
      'Nismo mogli potvrditi vaš odabir. Možda je zabilježen, a možda nije. Provjerite ponovno prije novog odabira.',
    check: 'Provjeri ponovno',
    stillPending:
      'Poneva još nije obradila ovo. Obrada je možda i dalje u tijeku, pa se ništa drugo ne može odlučiti dok se ne dovrši.',
    stillUnknown:
      'Nismo mogli dobiti odgovor. Ništa se drugo ne može odlučiti dok se ovo ne riješi. Pokušajte ponovno za koji trenutak.',
    requestClosed:
      'Ovaj je zahtjev zatvoren. Zatražite od agenta da se ponovno poveže kako biste pokrenuli novi zahtjev.',
    recordedOther: 'Drugi je odabir već zabilježen: {choice}. Vaš odabir nije primijenjen.',
    choiceAllow: 'dopušteno',
    choiceDeny: 'nije dopušteno',
    staleOnSend:
      'Vaš se pristup promijenio od stvaranja ovog zahtjeva. Ništa nije odobreno. Zatražite od agenta da se ponovno poveže.',
    refusedOnSend: 'Poneva je odbila ovaj zahtjev. Ništa nije odobreno.',
    noAccess: 'Nema pristupa',
    lastSeenNever: 'Još nije upotrijebljeno',
    lastSeenStale: 'Nije viđeno nedavno · zadnji put viđeno {when}',
    lastSeen: 'Zadnji put viđeno {when}',
    stateActive: 'Povezano',
    stateRevoked: 'Opozvano',
    stateExpired: 'Isteklo',
    needsConsent: 'Traži veći pristup',
    review: 'Pregledaj zahtjev',
    revoke: 'Opozovi',
    revokeConfirm:
      'Opozvati {client}? Odmah će prestati raditi. Možete ga ponovno povezati kasnije.',
    revokeYes: 'Opozovi',
    revokeNo: 'Zadrži',
    revokeFailed: 'Nismo mogli opozvati pristup. Možda je i dalje povezan. Pokušajte ponovno.',
    revokeDenied: 'Ne možete opozvati ovog agenta.',
    revokeGone: 'Ovaj agent više ne postoji. Popis prikazuje trenutačno stanje.',
    yourAgents: 'Vaši agenti',
    companyAgents: 'Agenti svih korisnika',
    companyAgentsHelp:
      'Kao administrator tvrtke možete vidjeti i opozvati sve agente za programiranje.',
  },
  cs: {
    title: 'Kódovací agenti',
    intro:
      'Kódovací agenti, které spouštíte sami, mohou číst znalosti vaší společnosti a pracovat s aplikacemi. Každý z nich požaduje přesně potřebný přístup a bez vašeho souhlasu se nic dalšího nepovolí.',
    none: 'Zatím nejsou připojeni žádní kódovací agenti.',
    loading: 'Načítání kódovacích agentů…',
    error: 'Kódovací agenty se nepodařilo načíst. Zkuste to znovu.',
    consentTitle: 'Povolit AI agentovi používat {company}?',
    notVerified:
      'AI agent s názvem „{client}“ chce používat {company}. Poneva nemůže tento název ověřit.',
    notVerifiedOrigin:
      'AI agent s názvem „{client}“ na adrese {origin} chce používat {company}. Poneva nemůže tento název ověřit.',
    dataLine:
      'To, co čte, je odesíláno společnosti {vendor} v rámci vlastního tarifu uživatele {person}, mimo hosting Ponevy v EU.',
    expiryDays:
      'Přístup platí {days} dní. Kdykoli ho můžete odvolat v části Připojení → Kódovací agenti.',
    expiryDay:
      'Přístup platí {days} den. Kdykoli ho můžete odvolat v části Připojení → Kódovací agenti.',
    headingAsking: 'Požaduje',
    headingRefused: 'Tento požadavek byl zamítnut',
    headingConsented: 'Povolili jste',
    headingRevoked: 'Tento přístup byl odvolán',
    headingStale: 'Tento požadavek je zastaralý',
    availAsked: 'Bude povoleno',
    availAlready: 'Již povoleno',
    availNotAvailable: 'Nemáte k dispozici',
    notAvailableNote:
      'Vaše role ve společnosti {company} toto nezahrnuje, proto to nebude povoleno.',
    notePartly:
      'Některé požadované možnosti nemáte k dispozici. Pokud přístup povolíte, udělí se pouze přístup označený jako „Bude povoleno“.',
    noteBeyond:
      'Nemáte k dispozici nic z toho, co agent požaduje, takže není co povolit. Požádejte správce společnosti.',
    noteStale:
      'Přístup vaší společnosti se změnil poté, co agent požádal o přístup. Požádejte agenta, aby se připojil znovu, a zkontrolujte aktuální požadavek.',
    noteRevoked: 'Agent již tento přístup nemůže používat.',
    noteConsented:
      'Toto nastavení můžete kdykoli změnit nebo přístup odvolat v části Kódovací agenti.',
    refusalReadRequired:
      'Agent nemůže požádat pouze o přístup k aplikacím. Potřebuje také číst znalosti společnosti. Požádejte agenta, aby se znovu připojil s oběma oprávněními.',
    refusalGeneral: 'Požadavek byl zamítnut. Požádejte agenta, aby se připojil znovu.',
    allow: 'Povolit',
    deny: 'Zamítnout',
    working: 'Probíhá…',
    unconfirmed:
      'Vaši volbu se nepodařilo potvrdit. Nemusí být zaznamenána. Před dalším výběrem ji znovu zkontrolujte.',
    check: 'Zkontrolovat znovu',
    stillPending:
      'Poneva tento požadavek zatím nevyřídila. Stále se může zpracovávat, takže dokud nebude vyřízen, nelze rozhodnout o ničem dalším.',
    stillUnknown:
      'Nepodařilo se získat odpověď. Dokud nebude požadavek vyřízen, nelze rozhodnout o ničem dalším. Zkuste to za chvíli znovu.',
    requestClosed:
      'Tento požadavek je uzavřený. Požádejte agenta, aby se znovu připojil a vytvořil nový požadavek.',
    recordedOther: 'Již byla zaznamenána jiná volba: {choice}. Vaše volba nebyla použita.',
    choiceAllow: 'povoleno',
    choiceDeny: 'nepovoleno',
    staleOnSend:
      'Váš přístup se od vytvoření tohoto požadavku změnil. Nic nebylo povoleno. Požádejte agenta, aby se připojil znovu.',
    refusedOnSend: 'Poneva tento požadavek zamítla. Nic nebylo povoleno.',
    noAccess: 'Bez přístupu',
    lastSeenNever: 'Zatím nepoužito',
    lastSeenStale: 'V poslední době nezaznamenán · naposledy {when}',
    lastSeen: 'Naposledy zaznamenán {when}',
    stateActive: 'Připojeno',
    stateRevoked: 'Odvoláno',
    stateExpired: 'Platnost vypršela',
    needsConsent: 'Požaduje další přístup',
    review: 'Zkontrolovat požadavek',
    revoke: 'Odvolat',
    revokeConfirm:
      'Odvolat přístup pro {client}? Okamžitě přestane fungovat. Později ho můžete znovu připojit.',
    revokeYes: 'Odvolat',
    revokeNo: 'Ponechat',
    revokeFailed: 'Nepodařilo se ho odvolat. Možná je stále připojený. Zkuste to znovu.',
    revokeDenied: 'Tohoto agenta nemůžete odvolat.',
    revokeGone: 'Tento agent už neexistuje. Seznam zobrazuje aktuální stav.',
    yourAgents: 'Vaši agenti',
    companyAgents: 'Agenti všech',
    companyAgentsHelp:
      'Jako správce společnosti můžete zobrazit a odvolat každého kódovacího agenta.',
  },
  da: {
    title: 'Kodeagenter',
    intro:
      'De kodeagenter, du selv kører, kan læse din virksomheds viden og arbejde med apps. Hver agent anmoder kun om den adgang, den har brug for, og intet udvides uden din godkendelse.',
    none: 'Der er endnu ikke tilsluttet nogen kodeagenter.',
    loading: 'Indlæser kodeagenter…',
    error: 'Vi kunne ikke indlæse dine kodeagenter. Prøv igen.',
    consentTitle: 'Vil du give en AI-agent adgang til {company}?',
    notVerified:
      'En AI-agent, der kalder sig “{client}”, vil bruge {company}. Poneva kan ikke bekræfte navnet.',
    notVerifiedOrigin:
      'En AI-agent, der kalder sig “{client}” på {origin}, vil bruge {company}. Poneva kan ikke bekræfte navnet.',
    dataLine:
      'Det, den læser, sendes til {vendor} under {person}s egen aftale uden for Ponevas EU-hosting.',
    expiryDays:
      'Adgangen gælder i {days} dage. Du kan tilbagekalde den når som helst under Forbindelser → Kodeagenter.',
    expiryDay:
      'Adgangen gælder i {days} dag. Du kan tilbagekalde den når som helst under Forbindelser → Kodeagenter.',
    headingAsking: 'Den anmoder om',
    headingRefused: 'Denne anmodning blev afvist',
    headingConsented: 'Du gav adgang til',
    headingRevoked: 'Denne adgang blev tilbagekaldt',
    headingStale: 'Denne anmodning er forældet',
    availAsked: 'Bliver tilladt',
    availAlready: 'Allerede tilladt',
    availNotAvailable: 'Ikke tilgængelig for dig',
    notAvailableNote: 'Din rolle hos {company} omfatter ikke dette, så det bliver ikke givet.',
    notePartly:
      'Noget af det, den anmodede om, er ikke tilgængeligt for dig. Hvis du tillader det, gives kun den adgang, der er markeret “Bliver tilladt”.',
    noteBeyond:
      'Intet af det, den anmodede om, er tilgængeligt for dig, så der er intet at tillade. Spørg en virksomhedsadministrator.',
    noteStale:
      'Din virksomhedsadgang blev ændret, efter at agenten sendte anmodningen. Bed agenten om at oprette forbindelse igen, så du kan gennemgå den aktuelle anmodning.',
    noteRevoked: 'Agenten kan ikke længere bruge denne adgang.',
    noteConsented: 'Du kan ændre eller tilbagekalde dette når som helst under Kodeagenter.',
    refusalReadRequired:
      'En agent kan ikke anmode om appadgang alene. Den skal også kunne læse virksomhedens viden. Bed agenten om at oprette forbindelse igen med begge dele.',
    refusalGeneral: 'Anmodningen blev afvist. Bed agenten om at oprette forbindelse igen.',
    allow: 'Tillad',
    deny: 'Afvis',
    working: 'Arbejder…',
    unconfirmed:
      'Vi kunne ikke bekræfte dit valg. Det er muligvis blevet registreret, men det er ikke sikkert. Kontrollér igen, før du vælger på ny.',
    check: 'Kontrollér igen',
    stillPending:
      'Poneva har endnu ikke afgjort dette. Det kan stadig være under behandling, så intet andet kan afgøres, før det er afklaret.',
    stillUnknown:
      'Vi kunne ikke få et svar. Intet andet kan afgøres, før dette er afklaret. Prøv igen om lidt.',
    requestClosed:
      'Denne anmodning er lukket. Bed agenten om at oprette forbindelse igen for at starte en ny.',
    recordedOther: 'Et andet valg er allerede registreret: {choice}. Dit valg blev ikke anvendt.',
    choiceAllow: 'tilladt',
    choiceDeny: 'ikke tilladt',
    staleOnSend:
      'Din adgang blev ændret, siden denne anmodning blev oprettet. Intet blev givet. Bed agenten om at oprette forbindelse igen.',
    refusedOnSend: 'Poneva afviste denne anmodning. Intet blev givet.',
    noAccess: 'Ingen adgang',
    lastSeenNever: 'Ikke brugt endnu',
    lastSeenStale: 'Ikke set for nylig · sidst set {when}',
    lastSeen: 'Sidst set {when}',
    stateActive: 'Forbundet',
    stateRevoked: 'Tilbagekaldt',
    stateExpired: 'Udløbet',
    needsConsent: 'Anmoder om mere adgang',
    review: 'Gennemgå anmodning',
    revoke: 'Tilbagekald',
    revokeConfirm:
      'Tilbagekald {client}? Den holder straks op med at fungere. Du kan oprette forbindelse til den igen senere.',
    revokeYes: 'Tilbagekald',
    revokeNo: 'Behold',
    revokeFailed: 'Vi kunne ikke tilbagekalde den. Den kan stadig være forbundet. Prøv igen.',
    revokeDenied: 'Du kan ikke tilbagekalde denne agent.',
    revokeGone: 'Denne agent findes ikke længere. Listen viser den aktuelle status.',
    yourAgents: 'Dine agenter',
    companyAgents: 'Alles agenter',
    companyAgentsHelp: 'Som virksomhedsadministrator kan du se og tilbagekalde alle kodeagenter.',
  },
  nl: {
    title: 'Codeeragents',
    intro:
      'De codeeragents die je zelf uitvoert, kunnen de kennis van je bedrijf lezen en met Apps werken. Elke agent vraagt precies om de benodigde toegang, en niets wordt uitgebreid zonder jouw toestemming.',
    none: 'Er zijn nog geen codeeragents verbonden.',
    loading: 'Codeeragents worden geladen…',
    error: 'We konden je codeeragents niet laden. Probeer het opnieuw.',
    consentTitle: 'Toestaan dat een AI-agent {company} gebruikt?',
    notVerified:
      'Een AI-agent die zichzelf “{client}” noemt, wil {company} gebruiken. Poneva kan de naam niet verifiëren.',
    notVerifiedOrigin:
      'Een AI-agent die zichzelf “{client}” noemt op {origin}, wil {company} gebruiken. Poneva kan de naam niet verifiëren.',
    dataLine:
      'Wat de agent leest, gaat naar {vendor} onder het eigen abonnement van {person}, buiten de EU-hosting van Poneva.',
    expiryDays:
      'De toegang duurt {days} dagen. Je kunt deze op elk moment intrekken via Verbindingen → Codeeragents.',
    expiryDay:
      'De toegang duurt {days} dag. Je kunt deze op elk moment intrekken via Verbindingen → Codeeragents.',
    headingAsking: 'De agent vraagt om',
    headingRefused: 'Dit verzoek is geweigerd',
    headingConsented: 'Je hebt toegestaan',
    headingRevoked: 'Deze toegang is ingetrokken',
    headingStale: 'Dit verzoek is verouderd',
    availAsked: 'Wordt toegestaan',
    availAlready: 'Al toegestaan',
    availNotAvailable: 'Niet beschikbaar voor jou',
    notAvailableNote: 'Je rol bij {company} omvat dit niet, dus het wordt niet toegekend.',
    notePartly:
      'Een deel van wat de agent heeft gevraagd, is niet beschikbaar voor jou. Als je dit toestaat, wordt alleen de toegang met de markering “Wordt toegestaan” toegekend.',
    noteBeyond:
      'Niets van wat de agent heeft gevraagd, is beschikbaar voor jou, dus er kan niets worden toegestaan. Vraag het aan een bedrijfsbeheerder.',
    noteStale:
      'Je bedrijfstoegang is gewijzigd nadat de agent dit heeft gevraagd. Vraag de agent opnieuw verbinding te maken, zodat je het huidige verzoek kunt controleren.',
    noteRevoked: 'De agent kan deze toegang niet langer gebruiken.',
    noteConsented: 'Je kunt dit op elk moment wijzigen of intrekken via Codeeragents.',
    refusalReadRequired:
      'Een agent kan niet alleen toegang tot Apps aanvragen. De agent moet ook de kennis van het bedrijf kunnen lezen. Vraag de agent opnieuw verbinding te maken met beide toegangsrechten.',
    refusalGeneral: 'Het verzoek is geweigerd. Vraag de agent opnieuw verbinding te maken.',
    allow: 'Toestaan',
    deny: 'Annuleren',
    working: 'Bezig…',
    unconfirmed:
      'We konden je keuze niet bevestigen. Deze is mogelijk wel of niet geregistreerd. Controleer dit voordat je opnieuw een keuze maakt.',
    check: 'Opnieuw controleren',
    stillPending:
      'Poneva heeft dit nog niet verwerkt. Het verzoek wordt mogelijk nog verwerkt, dus er kan niets anders worden besloten totdat dit is afgerond.',
    stillUnknown:
      'We konden geen antwoord krijgen. Er kan niets anders worden besloten totdat dit is afgerond. Probeer het over een moment opnieuw.',
    requestClosed:
      'Dit verzoek is gesloten. Vraag de agent opnieuw verbinding te maken om een nieuw verzoek te starten.',
    recordedOther:
      'Er is al een andere keuze geregistreerd: {choice}. Jouw keuze is niet toegepast.',
    choiceAllow: 'toegestaan',
    choiceDeny: 'niet toegestaan',
    staleOnSend:
      'Je toegang is gewijzigd nadat dit verzoek is ingediend. Er is niets toegekend. Vraag de agent opnieuw verbinding te maken.',
    refusedOnSend: 'Poneva heeft dit verzoek geweigerd. Er is niets toegekend.',
    noAccess: 'Geen toegang',
    lastSeenNever: 'Nog niet gebruikt',
    lastSeenStale: 'Niet recent gezien · laatst gezien {when}',
    lastSeen: 'Laatst gezien {when}',
    stateActive: 'Verbonden',
    stateRevoked: 'Ingetrokken',
    stateExpired: 'Verlopen',
    needsConsent: 'Vraagt om meer toegang',
    review: 'Verzoek controleren',
    revoke: 'Intrekken',
    revokeConfirm:
      '{client} intrekken? Het werkt dan meteen niet meer. Je kunt het later opnieuw verbinden.',
    revokeYes: 'Intrekken',
    revokeNo: 'Behouden',
    revokeFailed:
      'We konden het niet intrekken. Het is mogelijk nog steeds verbonden. Probeer het opnieuw.',
    revokeDenied: 'Je kunt deze agent niet intrekken.',
    revokeGone: 'Deze agent bestaat al niet meer. De lijst toont de huidige status.',
    yourAgents: 'Je agents',
    companyAgents: 'Agents van iedereen',
    companyAgentsHelp: 'Als bedrijfsbeheerder kun je elke codeeragent bekijken en intrekken.',
  },
  en: {
    title: 'Coding agents',
    intro:
      'The coding agents you run yourself can read your company’s knowledge and work with apps. Each one asks for exactly the access it needs, and nothing grows without your say-so.',
    none: 'No coding agents are connected yet.',
    loading: 'Loading coding agents…',
    error: 'We couldn’t load your coding agents. Try again.',
    consentTitle: 'Allow an AI agent to use {company}?',
    notVerified:
      'An AI agent that calls itself “{client}” wants to use {company}. Poneva can’t verify the name.',
    notVerifiedOrigin:
      'An AI agent that calls itself “{client}” on {origin} wants to use {company}. Poneva can’t verify the name.',
    dataLine:
      'What it reads goes to {vendor} under {person}’s own plan, outside Poneva’s EU hosting.',
    expiryDays:
      'Access lasts {days} days. You can revoke it any time in Connections → Coding agents.',
    expiryDay:
      'Access lasts {days} day. You can revoke it any time in Connections → Coding agents.',
    headingAsking: 'It asks to',
    headingRefused: 'This request was refused',
    headingConsented: 'You allowed',
    headingRevoked: 'This access was revoked',
    headingStale: 'This request is out of date',
    availAsked: 'Will be allowed',
    availAlready: 'Already allowed',
    availNotAvailable: 'Not available to you',
    notAvailableNote: 'Your role at {company} doesn’t include this, so it won’t be granted.',
    notePartly:
      'Some of what it asked for isn’t available to you. If you allow it, only the access marked “Will be allowed” is granted.',
    noteBeyond:
      'None of what it asked for is available to you, so there is nothing to allow. Ask a company admin.',
    noteStale:
      'Your company access changed after the agent asked. Ask the agent to connect again so you review the current request.',
    noteRevoked: 'The agent can no longer use this access.',
    noteConsented: 'You can change or revoke this at any time under Coding agents.',
    refusalReadRequired:
      'An agent can’t ask for app access alone. It also needs to read company knowledge. Ask the agent to connect again with both.',
    refusalGeneral: 'The request was refused. Ask the agent to connect again.',
    allow: 'Allow',
    deny: 'Cancel',
    working: 'Working…',
    unconfirmed:
      'We could not confirm your choice. It may or may not have been recorded. Check again before choosing once more.',
    check: 'Check again',
    stillPending:
      'Poneva has not settled this yet. It may still be going through, so nothing else can be decided until it is.',
    stillUnknown:
      'We could not get an answer. Nothing else can be decided until this is settled. Try again in a moment.',
    requestClosed: 'This request is closed. Ask the agent to connect again to start a new one.',
    recordedOther: 'Another choice was already recorded: {choice}. Your choice was not applied.',
    choiceAllow: 'allowed',
    choiceDeny: 'not allowed',
    staleOnSend:
      'Your access changed since this request was made. Nothing was granted. Ask the agent to connect again.',
    refusedOnSend: 'Poneva refused this request. Nothing was granted.',
    noAccess: 'No access',
    lastSeenNever: 'Not used yet',
    lastSeenStale: 'Not seen recently · last seen {when}',
    lastSeen: 'Last seen {when}',
    stateActive: 'Connected',
    stateRevoked: 'Revoked',
    stateExpired: 'Expired',
    needsConsent: 'Asking for more access',
    review: 'Review request',
    revoke: 'Revoke',
    revokeConfirm: 'Revoke {client}? It stops working at once. You can connect it again later.',
    revokeYes: 'Revoke',
    revokeNo: 'Keep',
    revokeFailed: 'We couldn’t revoke it. It may still be connected. Try again.',
    revokeDenied: 'You can’t revoke this agent.',
    revokeGone: 'This agent is already gone. The list shows the current state.',
    yourAgents: 'Your agents',
    companyAgents: 'Everyone’s agents',
    companyAgentsHelp: 'As a company admin you can see and revoke every coding agent.',
  },
  et: {
    title: 'Kodeerimisagendid',
    intro:
      'Teie käivitatavad kodeerimisagendid saavad lugeda teie ettevõtte teadmisi ja töötada rakendustega. Iga agent küsib täpselt vajalikku juurdepääsu ning midagi ei laiene ilma teie nõusolekuta.',
    none: 'Ühtegi kodeerimisagenti pole veel ühendatud.',
    loading: 'Kodeerimisagente laaditakse…',
    error: 'Me ei saanud teie kodeerimisagente laadida. Proovige uuesti.',
    consentTitle: 'Kas lubada AI-agendil kasutada ettevõtet {company}?',
    notVerified:
      'AI-agent nimega „{client}” soovib kasutada ettevõtet {company}. Poneva ei saa nime kinnitada.',
    notVerifiedOrigin:
      'AI-agent nimega „{client}” aadressil {origin} soovib kasutada ettevõtet {company}. Poneva ei saa nime kinnitada.',
    dataLine:
      'Loetud andmed lähevad teenusepakkujale {vendor} {person}i enda paketi alusel, väljaspool Poneva EL-i majutust.',
    expiryDays:
      'Juurdepääs kestab {days} päeva. Saate selle igal ajal tühistada jaotises Ühendused → Kodeerimisagendid.',
    expiryDay:
      'Juurdepääs kestab {days} päeva. Saate selle igal ajal tühistada jaotises Ühendused → Kodeerimisagendid.',
    headingAsking: 'Taotletud juurdepääs',
    headingRefused: 'See taotlus lükati tagasi',
    headingConsented: 'Teie lubatud',
    headingRevoked: 'See juurdepääs tühistati',
    headingStale: 'See taotlus on aegunud',
    availAsked: 'Lubatakse',
    availAlready: 'Juba lubatud',
    availNotAvailable: 'Te pole selleks volitatud',
    notAvailableNote: 'Teie roll ettevõttes {company} ei hõlma seda, seega seda ei anta.',
    notePartly:
      'Osa taotletust pole teile saadaval. Kui lubate, antakse ainult juurdepääs, mille juures on märge „Lubatakse“.',
    noteBeyond:
      'Ükski taotletud juurdepääs pole teile saadaval, seega pole midagi lubada. Küsige ettevõtte administraatorilt.',
    noteStale:
      'Teie ettevõtte juurdepääs muutus pärast agendi taotlust. Paluge agendil uuesti ühenduda, et saaksite praeguse taotluse üle vaadata.',
    noteRevoked: 'Agent ei saa seda juurdepääsu enam kasutada.',
    noteConsented: 'Saate seda igal ajal muuta või tühistada jaotises Kodeerimisagendid.',
    refusalReadRequired:
      'Agent ei saa taotleda ainult rakenduse juurdepääsu. Samuti peab see saama lugeda ettevõtte teadmisi. Paluge agendil uuesti ühenduda ja taotleda mõlemat.',
    refusalGeneral: 'Taotlus lükati tagasi. Paluge agendil uuesti ühenduda.',
    allow: 'Luba',
    deny: 'Keeldu',
    working: 'Töötlemine…',
    unconfirmed:
      'Me ei saanud teie valikut kinnitada. Seda võidi salvestada või mitte. Kontrollige enne uuesti valimist.',
    check: 'Kontrolli uuesti',
    stillPending:
      'Poneva pole seda veel kinnitanud. See võib alles töötluses olla, seega ei saa enne selle lahendamist midagi muud otsustada.',
    stillUnknown:
      'Me ei saanud vastust. Enne selle lahendamist ei saa midagi muud otsustada. Proovige hetke pärast uuesti.',
    requestClosed:
      'See taotlus on suletud. Uue taotluse alustamiseks paluge agendil uuesti ühenduda.',
    recordedOther: 'Teine valik on juba salvestatud: {choice}. Teie valikut ei rakendatud.',
    choiceAllow: 'lubatud',
    choiceDeny: 'mittelubatud',
    staleOnSend:
      'Teie juurdepääs muutus pärast selle taotluse esitamist. Midagi ei antud. Paluge agendil uuesti ühenduda.',
    refusedOnSend: 'Poneva lükkas selle taotluse tagasi. Midagi ei antud.',
    noAccess: 'Juurdepääs puudub',
    lastSeenNever: 'Veel kasutamata',
    lastSeenStale: 'Pole hiljuti nähtud · viimati nähtud {when}',
    lastSeen: 'Viimati nähtud {when}',
    stateActive: 'Ühendatud',
    stateRevoked: 'Tühistatud',
    stateExpired: 'Aegunud',
    needsConsent: 'Taotleb suuremat juurdepääsu',
    review: 'Vaata taotlus üle',
    revoke: 'Tühista',
    revokeConfirm:
      'Kas tühistada {client}? See lõpetab kohe töötamise. Saate selle hiljem uuesti ühendada.',
    revokeYes: 'Tühista',
    revokeNo: 'Säilita',
    revokeFailed: 'Tühistamine ebaõnnestus. See võib olla endiselt ühendatud. Proovi uuesti.',
    revokeDenied: 'Sa ei saa selle agendi juurdepääsu tühistada.',
    revokeGone: 'See agent on juba eemaldatud. Loend näitab praegust olekut.',
    yourAgents: 'Sinu agendid',
    companyAgents: 'Kõigi agendid',
    companyAgentsHelp:
      'Ettevõtte administraatorina näed ja saad tühistada kõigi kodeerimisagentide juurdepääsu.',
  },
  fi: {
    title: 'Koodausagentit',
    intro:
      'Itse käyttämäsi koodausagentit voivat lukea yrityksesi tietoja ja käyttää sovelluksia. Jokainen pyytää tarkalleen tarvitsemansa käyttöoikeudet, eikä mikään laajene ilman lupaasi.',
    none: 'Koodausagentteja ei ole vielä yhdistetty.',
    loading: 'Koodausagentteja ladataan…',
    error: 'Koodausagenttien lataaminen epäonnistui. Yritä uudelleen.',
    consentTitle: 'Sallitko tekoälyagentin käyttää yritystä {company}?',
    notVerified:
      'Tekoälyagentti, joka käyttää nimeä ”{client}”, haluaa käyttää yritystä {company}. Poneva ei voi vahvistaa nimeä.',
    notVerifiedOrigin:
      'Tekoälyagentti, joka käyttää nimeä ”{client}” osoitteessa {origin}, haluaa käyttää yritystä {company}. Poneva ei voi vahvistaa nimeä.',
    dataLine:
      'Sen lukemat tiedot lähetetään palveluntarjoajalle {vendor} henkilön {person} omalla sopimuksella Ponevan EU-isännöinnin ulkopuolelle.',
    expiryDays:
      'Käyttöoikeus on voimassa {days} päivää. Voit peruuttaa sen milloin tahansa kohdassa Yhteydet → Koodausagentit.',
    expiryDay:
      'Käyttöoikeus on voimassa {days} päivän. Voit peruuttaa sen milloin tahansa kohdassa Yhteydet → Koodausagentit.',
    headingAsking: 'Pyyntö koskee',
    headingRefused: 'Tämä pyyntö hylättiin',
    headingConsented: 'Olet sallinut',
    headingRevoked: 'Tämä käyttöoikeus on peruutettu',
    headingStale: 'Tämä pyyntö on vanhentunut',
    availAsked: 'Sallitaan',
    availAlready: 'Jo sallittu',
    availNotAvailable: 'Ei käytettävissäsi',
    notAvailableNote:
      'Roolisi yrityksessä {company} ei sisällä tätä käyttöoikeutta, joten sitä ei myönnetä.',
    notePartly:
      'Kaikki agentin pyytämät käyttöoikeudet eivät ole käytettävissäsi. Jos sallit pyynnön, myönnetään vain käyttöoikeudet, joiden kohdalla lukee ”Sallitaan”.',
    noteBeyond:
      'Mikään agentin pyytämistä käyttöoikeuksista ei ole käytettävissäsi, joten mitään ei voida sallia. Pyydä yrityksen ylläpitäjää apuun.',
    noteStale:
      'Yrityksen käyttöoikeutesi muuttuivat agentin pyynnön jälkeen. Pyydä agenttia yhdistämään uudelleen, jotta voit tarkistaa ajantasaisen pyynnön.',
    noteRevoked: 'Agentti ei voi enää käyttää tätä käyttöoikeutta.',
    noteConsented: 'Voit muuttaa tai peruuttaa tämän milloin tahansa kohdassa Koodausagentit.',
    refusalReadRequired:
      'Agentti ei voi pyytää pelkästään sovellusten käyttöoikeuksia. Sen on myös voitava lukea yrityksen tietoja. Pyydä agenttia yhdistämään uudelleen molempien käyttöoikeuksien kanssa.',
    refusalGeneral: 'Pyyntö hylättiin. Pyydä agenttia yhdistämään uudelleen.',
    allow: 'Salli',
    deny: 'Hylkää',
    working: 'Käsitellään…',
    unconfirmed:
      'Valintaasi ei voitu vahvistaa. Se on ehkä tallennettu tai sitten ei. Tarkista tilanne ennen kuin valitset uudelleen.',
    check: 'Tarkista uudelleen',
    stillPending:
      'Poneva ei ole vielä käsitellyt tätä loppuun. Pyyntö voi olla edelleen käsittelyssä, joten mitään muuta ei voida päättää ennen sen valmistumista.',
    stillUnknown:
      'Emme saaneet vastausta. Mitään muuta ei voida päättää ennen tämän käsittelyn valmistumista. Yritä hetken kuluttua uudelleen.',
    requestClosed:
      'Tämä pyyntö on suljettu. Pyydä agenttia yhdistämään uudelleen uuden pyynnön aloittamiseksi.',
    recordedOther: 'Toinen valinta on jo tallennettu: {choice}. Valintaasi ei otettu käyttöön.',
    choiceAllow: 'sallittu',
    choiceDeny: 'ei sallittu',
    staleOnSend:
      'Käyttöoikeutesi muuttuivat tämän pyynnön tekemisen jälkeen. Mitään ei myönnetty. Pyydä agenttia yhdistämään uudelleen.',
    refusedOnSend: 'Poneva hylkäsi tämän pyynnön. Mitään ei myönnetty.',
    noAccess: 'Ei käyttöoikeutta',
    lastSeenNever: 'Ei vielä käytetty',
    lastSeenStale: 'Ei havaittu äskettäin · viimeksi {when}',
    lastSeen: 'Viimeksi havaittu {when}',
    stateActive: 'Yhdistetty',
    stateRevoked: 'Peruutettu',
    stateExpired: 'Vanhentunut',
    needsConsent: 'Pyytää lisäkäyttöoikeuksia',
    review: 'Tarkista pyyntö',
    revoke: 'Peruuta',
    revokeConfirm:
      'Peruutetaanko {client}? Se lakkaa toimimasta heti. Voit yhdistää sen myöhemmin uudelleen.',
    revokeYes: 'Poista käyttöoikeus',
    revokeNo: 'Pidä',
    revokeFailed:
      'Käyttöoikeuden poistaminen epäonnistui. Yhteys voi olla edelleen käytössä. Yritä uudelleen.',
    revokeDenied: 'Et voi poistaa tämän agentin käyttöoikeutta.',
    revokeGone: 'Tämä agentti on jo poistettu. Luettelo näyttää nykyisen tilanteen.',
    yourAgents: 'Omat agentit',
    companyAgents: 'Yrityksen kaikki agentit',
    companyAgentsHelp:
      'Yrityksen ylläpitäjänä voit nähdä kaikkien koodausagentit ja poistaa niiden käyttöoikeudet.',
  },
  fr: {
    title: 'Agents de programmation',
    intro:
      'Les agents de programmation que vous exécutez vous-même peuvent consulter les connaissances de votre entreprise et utiliser des applications. Chacun demande exactement les accès dont il a besoin, et aucun accès supplémentaire n’est accordé sans votre autorisation.',
    none: 'Aucun agent de programmation n’est encore connecté.',
    loading: 'Chargement des agents de programmation…',
    error: 'Nous n’avons pas pu charger vos agents de programmation. Réessayez.',
    consentTitle: 'Autoriser un agent IA à utiliser {company} ?',
    notVerified:
      'Un agent IA se présentant comme « {client} » veut utiliser {company}. Poneva ne peut pas vérifier ce nom.',
    notVerifiedOrigin:
      'Un agent IA se présentant comme « {client} » sur {origin} veut utiliser {company}. Poneva ne peut pas vérifier ce nom.',
    dataLine:
      'Les données consultées sont transmises à {vendor} dans le cadre de l’offre personnelle de {person}, en dehors de l’hébergement européen de Poneva.',
    expiryDays:
      'L’accès dure {days} jours. Vous pouvez le révoquer à tout moment dans Connexions → Agents de programmation.',
    expiryDay:
      'L’accès dure {days} jour. Vous pouvez le révoquer à tout moment dans Connexions → Agents de programmation.',
    headingAsking: 'Il demande à',
    headingRefused: 'Cette demande a été refusée',
    headingConsented: 'Vous avez autorisé',
    headingRevoked: 'Cet accès a été révoqué',
    headingStale: 'Cette demande n’est plus à jour',
    availAsked: 'Sera autorisé',
    availAlready: 'Déjà autorisé',
    availNotAvailable: 'Non disponible pour vous',
    notAvailableNote:
      'Votre rôle dans {company} ne vous donne pas cet accès, qui ne sera donc pas accordé.',
    notePartly:
      'Une partie des accès demandés ne vous est pas disponible. Si vous l’autorisez, seuls les accès marqués « Sera autorisé » seront accordés.',
    noteBeyond:
      'Aucun des accès demandés ne vous est disponible ; il n’y a donc rien à autoriser. Demandez à un administrateur de l’entreprise.',
    noteStale:
      'Vos accès à l’entreprise ont changé après la demande de l’agent. Demandez à l’agent de se reconnecter afin de vérifier la demande actuelle.',
    noteRevoked: 'L’agent ne peut plus utiliser cet accès.',
    noteConsented:
      'Vous pouvez modifier ou révoquer cet accès à tout moment dans Agents de programmation.',
    refusalReadRequired:
      'Un agent ne peut pas demander uniquement l’accès aux applications. Il doit également pouvoir consulter les connaissances de l’entreprise. Demandez à l’agent de se reconnecter avec les deux accès.',
    refusalGeneral: 'La demande a été refusée. Demandez à l’agent de se reconnecter.',
    allow: 'Autoriser',
    deny: 'Annuler',
    working: 'Traitement…',
    unconfirmed:
      'Nous n’avons pas pu confirmer votre choix. Il a peut-être été enregistré ou non. Vérifiez avant de choisir à nouveau.',
    check: 'Vérifier à nouveau',
    stillPending:
      'Poneva n’a pas encore traité cette demande. Elle est peut-être toujours en cours ; aucune autre décision ne peut donc être prise pour le moment.',
    stillUnknown:
      'Nous n’avons pas pu obtenir de réponse. Aucune autre décision ne peut être prise tant que ce point n’est pas réglé. Réessayez dans un instant.',
    requestClosed:
      'Cette demande est fermée. Demandez à l’agent de se reconnecter pour en créer une nouvelle.',
    recordedOther:
      'Un autre choix a déjà été enregistré : {choice}. Votre choix n’a pas été appliqué.',
    choiceAllow: 'autorisé',
    choiceDeny: 'non autorisé',
    staleOnSend:
      'Vos accès ont changé depuis la création de cette demande. Aucun accès n’a été accordé. Demandez à l’agent de se reconnecter.',
    refusedOnSend: 'Poneva a refusé cette demande. Aucun accès n’a été accordé.',
    noAccess: 'Aucun accès',
    lastSeenNever: 'Pas encore utilisé',
    lastSeenStale: 'Non vu récemment · dernière activité {when}',
    lastSeen: 'Dernière activité {when}',
    stateActive: 'Connecté',
    stateRevoked: 'Révoqué',
    stateExpired: 'Expiré',
    needsConsent: 'Demande davantage d’accès',
    review: 'Examiner la demande',
    revoke: 'Révoquer',
    revokeConfirm:
      'Révoquer {client} ? Il cessera immédiatement de fonctionner. Vous pourrez le reconnecter plus tard.',
    revokeYes: 'Révoquer',
    revokeNo: 'Conserver',
    revokeFailed: 'Nous n’avons pas pu le révoquer. Il est peut-être toujours connecté. Réessayez.',
    revokeDenied: 'Vous ne pouvez pas révoquer cet agent.',
    revokeGone: 'Cet agent a déjà disparu. La liste affiche l’état actuel.',
    yourAgents: 'Vos agents',
    companyAgents: 'Agents de tout le monde',
    companyAgentsHelp:
      'En tant qu’administrateur de l’entreprise, vous pouvez voir et révoquer tous les agents de programmation.',
  },
  de: {
    title: 'Coding-Agenten',
    intro:
      'Die Coding-Agenten, die Sie selbst ausführen, können das Wissen Ihres Unternehmens lesen und mit Apps arbeiten. Jeder fragt genau nach den benötigten Berechtigungen, und ohne Ihre Zustimmung kommen keine weiteren hinzu.',
    none: 'Noch keine Coding-Agenten verbunden.',
    loading: 'Coding-Agenten werden geladen…',
    error: 'Wir konnten Ihre Coding-Agenten nicht laden. Versuchen Sie es erneut.',
    consentTitle: 'Darf ein KI-Agent {company} verwenden?',
    notVerified:
      'Ein KI-Agent namens „{client}“ möchte {company} verwenden. Poneva kann den Namen nicht überprüfen.',
    notVerifiedOrigin:
      'Ein KI-Agent namens „{client}“ auf {origin} möchte {company} verwenden. Poneva kann den Namen nicht überprüfen.',
    dataLine:
      'Gelesene Daten werden unter dem eigenen Tarif von {person} an {vendor} übermittelt, außerhalb des EU-Hostings von Poneva.',
    expiryDays:
      'Der Zugriff gilt {days} Tage. Sie können ihn jederzeit unter Verbindungen → Coding-Agenten widerrufen.',
    expiryDay:
      'Der Zugriff gilt {days} Tag. Sie können ihn jederzeit unter Verbindungen → Coding-Agenten widerrufen.',
    headingAsking: 'Angefordert',
    headingRefused: 'Diese Anfrage wurde abgelehnt',
    headingConsented: 'Sie haben Zugriff gewährt',
    headingRevoked: 'Dieser Zugriff wurde widerrufen',
    headingStale: 'Diese Anfrage ist nicht mehr aktuell',
    availAsked: 'Wird gewährt',
    availAlready: 'Bereits gewährt',
    availNotAvailable: 'Für Sie nicht verfügbar',
    notAvailableNote:
      'Ihre Rolle bei {company} umfasst dies nicht, daher wird der Zugriff nicht gewährt.',
    notePartly:
      'Ein Teil der angeforderten Berechtigungen ist für Sie nicht verfügbar. Wenn Sie den Zugriff gewähren, wird nur der mit „Wird gewährt“ markierte Zugriff gewährt.',
    noteBeyond:
      'Keine der angeforderten Berechtigungen ist für Sie verfügbar, daher gibt es nichts zu gewähren. Wenden Sie sich an einen Unternehmensadministrator.',
    noteStale:
      'Ihr Unternehmenszugriff wurde geändert, nachdem der Agent die Anfrage gestellt hat. Bitten Sie den Agenten, die Verbindung erneut herzustellen, damit Sie die aktuelle Anfrage prüfen können.',
    noteRevoked: 'Der Agent kann diesen Zugriff nicht mehr verwenden.',
    noteConsented: 'Sie können dies jederzeit unter Coding-Agenten ändern oder widerrufen.',
    refusalReadRequired:
      'Ein Agent kann nicht allein nach App-Zugriff fragen. Er muss auch das Unternehmenswissen lesen können. Bitten Sie den Agenten, die Verbindung erneut mit beiden Berechtigungen herzustellen.',
    refusalGeneral:
      'Die Anfrage wurde abgelehnt. Bitten Sie den Agenten, die Verbindung erneut herzustellen.',
    allow: 'Zulassen',
    deny: 'Abbrechen',
    working: 'Wird verarbeitet…',
    unconfirmed:
      'Wir konnten Ihre Auswahl nicht bestätigen. Sie wurde möglicherweise aufgezeichnet oder auch nicht. Überprüfen Sie dies, bevor Sie erneut eine Auswahl treffen.',
    check: 'Erneut prüfen',
    stillPending:
      'Poneva hat dies noch nicht abgeschlossen. Die Verarbeitung kann noch laufen, daher kann bis dahin nichts Weiteres entschieden werden.',
    stillUnknown:
      'Wir konnten keine Antwort erhalten. Bis dies geklärt ist, kann nichts Weiteres entschieden werden. Versuchen Sie es gleich noch einmal.',
    requestClosed:
      'Diese Anfrage ist geschlossen. Bitten Sie den Agenten, die Verbindung erneut herzustellen, um eine neue Anfrage zu starten.',
    recordedOther:
      'Es wurde bereits eine andere Auswahl aufgezeichnet: {choice}. Ihre Auswahl wurde nicht angewendet.',
    choiceAllow: 'zugelassen',
    choiceDeny: 'nicht zugelassen',
    staleOnSend:
      'Ihr Zugriff wurde geändert, seit diese Anfrage gestellt wurde. Es wurde kein Zugriff gewährt. Bitten Sie den Agenten, die Verbindung erneut herzustellen.',
    refusedOnSend: 'Poneva hat diese Anfrage abgelehnt. Es wurde kein Zugriff gewährt.',
    noAccess: 'Kein Zugriff',
    lastSeenNever: 'Noch nicht verwendet',
    lastSeenStale: 'Lange nicht gesehen · zuletzt gesehen {when}',
    lastSeen: 'Zuletzt gesehen {when}',
    stateActive: 'Verbunden',
    stateRevoked: 'Widerrufen',
    stateExpired: 'Abgelaufen',
    needsConsent: 'Fordert mehr Zugriff an',
    review: 'Anfrage prüfen',
    revoke: 'Widerrufen',
    revokeConfirm:
      '{client} widerrufen? Dadurch wird der Agent sofort funktionsunfähig. Sie können ihn später erneut verbinden.',
    revokeYes: 'Widerrufen',
    revokeNo: 'Beibehalten',
    revokeFailed:
      'Der Widerruf ist fehlgeschlagen. Möglicherweise ist der Agent noch verbunden. Versuchen Sie es erneut.',
    revokeDenied: 'Sie können diesen Agenten nicht widerrufen.',
    revokeGone:
      'Dieser Agent ist bereits nicht mehr vorhanden. Die Liste zeigt den aktuellen Stand.',
    yourAgents: 'Ihre Agenten',
    companyAgents: 'Agenten aller',
    companyAgentsHelp:
      'Als Unternehmensadministrator können Sie jeden Coding-Agenten sehen und widerrufen.',
  },
  el: {
    title: 'Πράκτορες προγραμματισμού',
    intro:
      'Οι πράκτορες προγραμματισμού που εκτελείτε οι ίδιοι μπορούν να διαβάζουν τη γνώση της εταιρείας σας και να συνεργάζονται με εφαρμογές. Ο καθένας ζητά ακριβώς την πρόσβαση που χρειάζεται και τίποτα περισσότερο δεν ενεργοποιείται χωρίς την έγκρισή σας.',
    none: 'Δεν υπάρχουν ακόμη συνδεδεμένοι πράκτορες προγραμματισμού.',
    loading: 'Φόρτωση πρακτόρων προγραμματισμού…',
    error: 'Δεν μπορέσαμε να φορτώσουμε τους πράκτορες προγραμματισμού σας. Δοκιμάστε ξανά.',
    consentTitle: 'Να επιτραπεί σε έναν πράκτορα AI να χρησιμοποιήσει την {company};',
    notVerified:
      'Ένας πράκτορας AI που δηλώνει «{client}» θέλει να χρησιμοποιήσει την {company}. Το Poneva δεν μπορεί να επαληθεύσει το όνομα.',
    notVerifiedOrigin:
      'Ένας πράκτορας AI που δηλώνει «{client}» στο {origin} θέλει να χρησιμοποιήσει την {company}. Το Poneva δεν μπορεί να επαληθεύσει το όνομα.',
    dataLine:
      'Ό,τι διαβάζει αποστέλλεται στη {vendor} μέσω του προσωπικού προγράμματος του/της {person}, εκτός της φιλοξενίας του Poneva στην ΕΕ.',
    expiryDays:
      'Η πρόσβαση διαρκεί {days} ημέρες. Μπορείτε να την ανακαλέσετε ανά πάσα στιγμή από τις Συνδέσεις → Πράκτορες προγραμματισμού.',
    expiryDay:
      'Η πρόσβαση διαρκεί {days} ημέρα. Μπορείτε να την ανακαλέσετε ανά πάσα στιγμή από τις Συνδέσεις → Πράκτορες προγραμματισμού.',
    headingAsking: 'Ζητά',
    headingRefused: 'Αυτό το αίτημα απορρίφθηκε',
    headingConsented: 'Επιτρέψατε',
    headingRevoked: 'Αυτή η πρόσβαση ανακλήθηκε',
    headingStale: 'Αυτό το αίτημα δεν είναι πλέον επίκαιρο',
    availAsked: 'Θα επιτραπεί',
    availAlready: 'Έχει ήδη επιτραπεί',
    availNotAvailable: 'Δεν είναι διαθέσιμο σε εσάς',
    notAvailableNote:
      'Ο ρόλος σας στην {company} δεν περιλαμβάνει αυτήν την πρόσβαση, επομένως δεν θα παραχωρηθεί.',
    notePartly:
      'Κάποια από όσα ζητήθηκαν δεν είναι διαθέσιμα σε εσάς. Αν το επιτρέψετε, θα παραχωρηθεί μόνο η πρόσβαση που επισημαίνεται ως «Θα επιτραπεί».',
    noteBeyond:
      'Τίποτα από όσα ζητήθηκαν δεν είναι διαθέσιμο σε εσάς, επομένως δεν υπάρχει κάτι που μπορείτε να επιτρέψετε. Ρωτήστε έναν διαχειριστή της εταιρείας.',
    noteStale:
      'Η πρόσβασή σας στην εταιρεία άλλαξε αφού το ζήτησε ο πράκτορας. Ζητήστε από τον πράκτορα να συνδεθεί ξανά, ώστε να ελέγξετε το τρέχον αίτημα.',
    noteRevoked: 'Ο πράκτορας δεν μπορεί πλέον να χρησιμοποιήσει αυτήν την πρόσβαση.',
    noteConsented:
      'Μπορείτε να το αλλάξετε ή να το ανακαλέσετε ανά πάσα στιγμή από την ενότητα «Πράκτορες προγραμματισμού».',
    refusalReadRequired:
      'Ένας πράκτορας δεν μπορεί να ζητήσει μόνο πρόσβαση σε εφαρμογές. Χρειάζεται επίσης να διαβάζει τη γνώση της εταιρείας. Ζητήστε από τον πράκτορα να συνδεθεί ξανά και με τα δύο.',
    refusalGeneral: 'Το αίτημα απορρίφθηκε. Ζητήστε από τον πράκτορα να συνδεθεί ξανά.',
    allow: 'Επίτρεψη',
    deny: 'Ακύρωση',
    working: 'Επεξεργασία…',
    unconfirmed:
      'Δεν μπορέσαμε να επιβεβαιώσουμε την επιλογή σας. Ενδέχεται να καταγράφηκε ή να μην καταγράφηκε. Ελέγξτε ξανά πριν επιλέξετε πάλι.',
    check: 'Έλεγχος ξανά',
    stillPending:
      'Το Poneva δεν έχει οριστικοποιήσει ακόμη αυτό το αίτημα. Ενδέχεται να βρίσκεται ακόμη σε εξέλιξη, επομένως δεν μπορεί να ληφθεί άλλη απόφαση μέχρι να ολοκληρωθεί.',
    stillUnknown:
      'Δεν μπορέσαμε να λάβουμε απάντηση. Δεν μπορεί να ληφθεί άλλη απόφαση μέχρι να οριστικοποιηθεί αυτό. Δοκιμάστε ξανά σε λίγο.',
    requestClosed:
      'Αυτό το αίτημα έχει κλείσει. Ζητήστε από τον πράκτορα να συνδεθεί ξανά για να ξεκινήσει ένα νέο αίτημα.',
    recordedOther: 'Έχει ήδη καταγραφεί άλλη επιλογή: {choice}. Η επιλογή σας δεν εφαρμόστηκε.',
    choiceAllow: 'επιτράπηκε',
    choiceDeny: 'δεν επιτράπηκε',
    staleOnSend:
      'Η πρόσβασή σας άλλαξε από τότε που υποβλήθηκε αυτό το αίτημα. Δεν παραχωρήθηκε καμία πρόσβαση. Ζητήστε από τον πράκτορα να συνδεθεί ξανά.',
    refusedOnSend: 'Το Poneva απέρριψε αυτό το αίτημα. Δεν παραχωρήθηκε καμία πρόσβαση.',
    noAccess: 'Χωρίς πρόσβαση',
    lastSeenNever: 'Δεν έχει χρησιμοποιηθεί ακόμη',
    lastSeenStale: 'Δεν έχει χρησιμοποιηθεί πρόσφατα · τελευταία χρήση {when}',
    lastSeen: 'Τελευταία χρήση {when}',
    stateActive: 'Συνδεδεμένος',
    stateRevoked: 'Ανακλήθηκε',
    stateExpired: 'Έληξε',
    needsConsent: 'Ζητά περισσότερη πρόσβαση',
    review: 'Έλεγχος αιτήματος',
    revoke: 'Ανάκληση',
    revokeConfirm:
      'Να ανακληθεί η πρόσβαση του/της {client}; Θα σταματήσει να λειτουργεί αμέσως. Μπορείτε να τον/την συνδέσετε ξανά αργότερα.',
    revokeYes: 'Ανάκληση',
    revokeNo: 'Διατήρηση',
    revokeFailed: 'Δεν ήταν δυνατή η ανάκλησή του. Ίσως είναι ακόμη συνδεδεμένο. Δοκιμάστε ξανά.',
    revokeDenied: 'Δεν μπορείτε να ανακαλέσετε αυτόν τον πράκτορα.',
    revokeGone: 'Αυτός ο πράκτορας έχει ήδη αφαιρεθεί. Η λίστα εμφανίζει την τρέχουσα κατάσταση.',
    yourAgents: 'Οι πράκτορές σας',
    companyAgents: 'Πράκτορες όλων',
    companyAgentsHelp:
      'Ως διαχειριστής της εταιρείας, μπορείτε να βλέπετε και να ανακαλείτε κάθε πράκτορα κώδικα.',
  },
  hu: {
    title: 'Kódolóügynökök',
    intro:
      'A saját maga által futtatott kódolóügynökök hozzáférhetnek cége tudásanyagához, és együttműködhetnek az alkalmazásokkal. Mindegyik csak a szükséges hozzáféréseket kéri, és semmi sem bővül az Ön jóváhagyása nélkül.',
    none: 'Még nincs csatlakoztatott kódolóügynök.',
    loading: 'A kódolóügynökök betöltése…',
    error: 'Nem sikerült betölteni a kódolóügynökeit. Próbálja újra.',
    consentTitle: 'Engedélyezi, hogy egy AI-ügynök használja a(z) {company} vállalatot?',
    notVerified:
      'Egy magát „{client}” néven azonosító AI-ügynök használni szeretné a(z) {company} vállalatot. A Poneva nem tudja ellenőrizni a nevet.',
    notVerifiedOrigin:
      'Egy magát a(z) {origin} helyen „{client}” néven azonosító AI-ügynök használni szeretné a(z) {company} vállalatot. A Poneva nem tudja ellenőrizni a nevet.',
    dataLine:
      'Az általa beolvasott adatok a(z) {vendor} szolgáltatóhoz kerülnek {person} saját csomagjának keretében, a Poneva EU-s tárhelyén kívül.',
    expiryDays:
      'A hozzáférés {days} napig érvényes. Bármikor visszavonhatja a Kapcsolatok → Kódolóügynökök menüpontban.',
    expiryDay:
      'A hozzáférés {days} napig érvényes. Bármikor visszavonhatja a Kapcsolatok → Kódolóügynökök menüpontban.',
    headingAsking: 'Ezt kéri',
    headingRefused: 'Ezt a kérelmet elutasították',
    headingConsented: 'Ezt engedélyezte',
    headingRevoked: 'Ezt a hozzáférést visszavonták',
    headingStale: 'Ez a kérelem már nem aktuális',
    availAsked: 'Engedélyezve lesz',
    availAlready: 'Már engedélyezve',
    availNotAvailable: 'Ön számára nem érhető el',
    notAvailableNote:
      'A(z) {company} vállalatnál betöltött szerepköre nem tartalmazza ezt, ezért nem lesz megadva.',
    notePartly:
      'A kért hozzáférések egy része nem érhető el Ön számára. Ha engedélyezi, csak az „Engedélyezve lesz” jelölésű hozzáférések lesznek megadva.',
    noteBeyond:
      'A kért hozzáférések egyike sem érhető el Ön számára, ezért nincs mit engedélyezni. Kérje a vállalat rendszergazdájának segítségét.',
    noteStale:
      'A vállalati hozzáférése megváltozott az ügynök kérelme óta. Kérje az ügynököt, hogy csatlakozzon újra, hogy áttekintheti az aktuális kérelmet.',
    noteRevoked: 'Az ügynök többé nem használhatja ezt a hozzáférést.',
    noteConsented: 'Ezt bármikor módosíthatja vagy visszavonhatja a Kódolóügynökök menüpontban.',
    refusalReadRequired:
      'Egy ügynök önmagában nem kérhet hozzáférést az alkalmazásokhoz. A vállalati tudásanyagot is olvasnia kell. Kérje az ügynököt, hogy mindkettővel csatlakozzon újra.',
    refusalGeneral: 'A kérelmet elutasították. Kérje az ügynököt, hogy csatlakozzon újra.',
    allow: 'Engedélyezés',
    deny: 'Elutasítás',
    working: 'Feldolgozás…',
    unconfirmed:
      'Nem sikerült megerősítenünk a választását. Lehet, hogy rögzítettük, de az is lehet, hogy nem. Újabb választás előtt ellenőrizze újra.',
    check: 'Ellenőrzés újra',
    stillPending:
      'A Poneva még nem zárta le ezt. Lehet, hogy a folyamat még zajlik, ezért a lezárásig nem hozható más döntés.',
    stillUnknown:
      'Nem sikerült választ kapnunk. A lezárásig nem hozható más döntés. Próbálja meg egy kis idő múlva.',
    requestClosed:
      'Ez a kérelem lezárult. Új kérelem indításához kérje az ügynököt, hogy csatlakozzon újra.',
    recordedOther:
      'Már rögzítettek egy másik választ: {choice}. Az Ön választása nem lett alkalmazva.',
    choiceAllow: 'engedélyezve',
    choiceDeny: 'nem engedélyezve',
    staleOnSend:
      'A hozzáférése megváltozott a kérelem létrehozása óta. Semmit sem engedélyeztünk. Kérje az ügynököt, hogy csatlakozzon újra.',
    refusedOnSend: 'A Poneva elutasította ezt a kérelmet. Semmit sem engedélyeztünk.',
    noAccess: 'Nincs hozzáférés',
    lastSeenNever: 'Még nem használta',
    lastSeenStale: 'Nemrég nem láttuk · utoljára ekkor: {when}',
    lastSeen: 'Utoljára ekkor láttuk: {when}',
    stateActive: 'Csatlakoztatva',
    stateRevoked: 'Visszavonva',
    stateExpired: 'Lejárt',
    needsConsent: 'További hozzáférést kér',
    review: 'Kérelem áttekintése',
    revoke: 'Visszavonás',
    revokeConfirm:
      'Visszavonja a(z) {client} hozzáférését? Azonnal leáll. Később újra csatlakoztathatja.',
    revokeYes: 'Visszavonás',
    revokeNo: 'Megtartás',
    revokeFailed:
      'Nem sikerült visszavonni. Lehet, hogy továbbra is csatlakoztatva van. Próbáld újra.',
    revokeDenied: 'Ezt az ügynököt nem vonhatod vissza.',
    revokeGone: 'Ez az ügynök már nem létezik. A lista az aktuális állapotot mutatja.',
    yourAgents: 'Saját ügynökeid',
    companyAgents: 'A vállalat ügynökei',
    companyAgentsHelp:
      'Vállalati adminisztrátorként megtekintheted és visszavonhatod az összes kódolóügynököt.',
  },
  ga: {
    title: 'Gníomhairí códaithe',
    intro:
      'Is féidir leis na gníomhairí códaithe a ritheann tú féin eolas do chuideachta a léamh agus oibriú le haipeanna. Iarrann gach ceann acu go díreach an rochtain a theastaíonn uaidh, agus ní mhéadaítear aon rud gan do chead.',
    none: 'Níl aon ghníomhairí códaithe ceangailte fós.',
    loading: 'Gníomhairí códaithe á lódáil…',
    error: 'Ní rabhamar in ann do ghníomhairí códaithe a lódáil. Bain triail eile as.',
    consentTitle: 'An gceadaíonn tú do ghníomhaire AI {company} a úsáid?',
    notVerified:
      'Tá gníomhaire AI a thugann “{client}” air féin ag iarraidh {company} a úsáid. Ní féidir le Poneva an t-ainm a fhíorú.',
    notVerifiedOrigin:
      'Tá gníomhaire AI a thugann “{client}” air féin ar {origin} ag iarraidh {company} a úsáid. Ní féidir le Poneva an t-ainm a fhíorú.',
    dataLine:
      'Seoltar an méid a léann sé chuig {vendor} faoi phlean {person} féin, lasmuigh d’óstáil Poneva san Aontas Eorpach.',
    expiryDays:
      'Maireann an rochtain ar feadh {days} lá. Is féidir leat í a chúlghairm am ar bith in Naisc → Gníomhairí códaithe.',
    expiryDay:
      'Maireann an rochtain ar feadh {days} lá. Is féidir leat í a chúlghairm am ar bith in Naisc → Gníomhairí códaithe.',
    headingAsking: 'Tá sé ag iarraidh',
    headingRefused: 'Diúltaíodh don iarratas seo',
    headingConsented: 'Cheadaigh tú',
    headingRevoked: 'Cúlghaireadh an rochtain seo',
    headingStale: 'Tá an t-iarratas seo as dáta',
    availAsked: 'Ceadófar',
    availAlready: 'Ceadaithe cheana',
    availNotAvailable: 'Níl sé ar fáil duit',
    notAvailableNote: 'Níl sé seo san áireamh i do ról in {company}, mar sin ní dheonófar é.',
    notePartly:
      'Níl cuid den mhéid a d’iarr sé ar fáil duit. Má cheadaíonn tú é, ní dheonófar ach an rochtain a bhfuil “Ceadófar” marcáilte uirthi.',
    noteBeyond:
      'Níl aon chuid den mhéid a d’iarr sé ar fáil duit, mar sin níl aon rud le ceadú. Cuir ceist ar riarthóir cuideachta.',
    noteStale:
      'Athraíodh do rochtain ar an gcuideachta tar éis don ghníomhaire iarraidh a dhéanamh. Iarr ar an ngníomhaire ceangal arís ionas gur féidir leat an t-iarratas reatha a athbhreithniú.',
    noteRevoked: 'Ní féidir leis an ngníomhaire an rochtain seo a úsáid a thuilleadh.',
    noteConsented:
      'Is féidir leat é seo a athrú nó a chúlghairm am ar bith faoi Gníomhairí códaithe.',
    refusalReadRequired:
      'Ní féidir le gníomhaire rochtain ar aipeanna a iarraidh leis féin. Caithfidh sé eolas na cuideachta a léamh freisin. Iarr ar an ngníomhaire ceangal arís leis an dá cheann.',
    refusalGeneral: 'Diúltaíodh don iarratas. Iarr ar an ngníomhaire ceangal arís.',
    allow: 'Ceadaigh',
    deny: 'Cealaigh',
    working: 'Ag obair…',
    unconfirmed:
      'Ní rabhamar in ann do rogha a dheimhniú. Seans gur taifeadadh í, nó seans nár taifeadadh. Seiceáil arís sula roghnaíonn tú arís.',
    check: 'Seiceáil arís',
    stillPending:
      'Níl Poneva tar éis é seo a shocrú fós. Seans go bhfuil sé á phróiseáil fós, mar sin ní féidir aon rud eile a chinneadh go dtí go socrófar é.',
    stillUnknown:
      'Ní rabhamar in ann freagra a fháil. Ní féidir aon rud eile a chinneadh go dtí go socrófar é seo. Bain triail eile as ar ball beag.',
    requestClosed:
      'Tá an t-iarratas seo dúnta. Iarr ar an ngníomhaire ceangal arís chun ceann nua a thosú.',
    recordedOther: 'Taifeadadh rogha eile cheana: {choice}. Níor cuireadh do rogha i bhfeidhm.',
    choiceAllow: 'ceadaithe',
    choiceDeny: 'neamhcheadaithe',
    staleOnSend:
      'Athraíodh do rochtain ó rinneadh an t-iarratas seo. Níor deonaíodh aon rud. Iarr ar an ngníomhaire ceangal arís.',
    refusedOnSend: 'Dhiúltaigh Poneva don iarratas seo. Níor deonaíodh aon rud.',
    noAccess: 'Gan rochtain',
    lastSeenNever: 'Gan úsáid fós',
    lastSeenStale: 'Ní fhacthas le déanaí é · feicthe go deireanach {when}',
    lastSeen: 'Feicthe go deireanach {when}',
    stateActive: 'Ceangailte',
    stateRevoked: 'Cúlghairthe',
    stateExpired: 'Imithe in éag',
    needsConsent: 'Ag iarraidh tuilleadh rochtana',
    review: 'Athbhreithnigh an t-iarratas',
    revoke: 'Cúlghairm',
    revokeConfirm:
      'An bhfuil fonn ort {client} a chúlghairm? Stopfaidh sé ag obair láithreach. Is féidir leat é a cheangal arís níos déanaí.',
    revokeYes: 'Cúlghairm',
    revokeNo: 'Coinnigh',
    revokeFailed:
      'Níorbh fhéidir linn é a chúlghairm. Seans go bhfuil sé fós ceangailte. Bain triail eile as.',
    revokeDenied: 'Ní féidir leat an gníomhaire seo a chúlghairm.',
    revokeGone: 'Tá an gníomhaire seo imithe cheana féin. Taispeánann an liosta an staid reatha.',
    yourAgents: 'Do ghníomhairí',
    companyAgents: 'Gníomhairí gach duine',
    companyAgentsHelp:
      'Mar riarthóir cuideachta, is féidir leat gach gníomhaire códúcháin a fheiceáil agus a chúlghairm.',
  },
  it: {
    title: 'Agenti di programmazione',
    intro:
      'Gli agenti di programmazione che esegui autonomamente possono leggere le conoscenze della tua azienda e lavorare con le app. Ognuno richiede esattamente gli accessi di cui ha bisogno e nulla viene aggiunto senza la tua approvazione.',
    none: 'Non ci sono ancora agenti di programmazione collegati.',
    loading: 'Caricamento degli agenti di programmazione…',
    error: 'Non abbiamo potuto caricare i tuoi agenti di programmazione. Riprova.',
    consentTitle: 'Consentire a un agente IA di usare {company}?',
    notVerified:
      'Un agente IA che si presenta come “{client}” vuole usare {company}. Poneva non può verificare il nome.',
    notVerifiedOrigin:
      'Un agente IA che si presenta come “{client}” su {origin} vuole usare {company}. Poneva non può verificare il nome.',
    dataLine:
      'Ciò che legge viene inviato a {vendor} nell’ambito del piano personale di {person}, al di fuori dell’hosting UE di Poneva.',
    expiryDays:
      'L’accesso dura {days} giorni. Puoi revocarlo in qualsiasi momento in Connessioni → Agenti di programmazione.',
    expiryDay:
      'L’accesso dura {days} giorno. Puoi revocarlo in qualsiasi momento in Connessioni → Agenti di programmazione.',
    headingAsking: 'Richiede',
    headingRefused: 'Questa richiesta è stata rifiutata',
    headingConsented: 'Hai consentito',
    headingRevoked: 'Questo accesso è stato revocato',
    headingStale: 'Questa richiesta non è più aggiornata',
    availAsked: 'Verrà consentito',
    availAlready: 'Già consentito',
    availNotAvailable: 'Non disponibile per te',
    notAvailableNote:
      'Il tuo ruolo in {company} non include questo accesso, quindi non verrà concesso.',
    notePartly:
      'Una parte degli accessi richiesti non è disponibile per te. Se lo consenti, verrà concesso solo l’accesso contrassegnato come “Verrà consentito”.',
    noteBeyond:
      'Nessuno degli accessi richiesti è disponibile per te, quindi non c’è nulla da consentire. Chiedi a un amministratore dell’azienda.',
    noteStale:
      'Il tuo accesso all’azienda è cambiato dopo la richiesta dell’agente. Chiedi all’agente di collegarsi di nuovo per esaminare la richiesta attuale.',
    noteRevoked: 'L’agente non può più usare questo accesso.',
    noteConsented:
      'Puoi modificare o revocare questo accesso in qualsiasi momento nella sezione Agenti di programmazione.',
    refusalReadRequired:
      'Un agente non può richiedere solo l’accesso alle app. Deve anche poter leggere le conoscenze dell’azienda. Chiedi all’agente di collegarsi di nuovo con entrambi gli accessi.',
    refusalGeneral: 'La richiesta è stata rifiutata. Chiedi all’agente di collegarsi di nuovo.',
    allow: 'Consenti',
    deny: 'Annulla',
    working: 'Elaborazione…',
    unconfirmed:
      'Non abbiamo potuto confermare la tua scelta. Potrebbe essere stata registrata oppure no. Controlla di nuovo prima di effettuare un’altra scelta.',
    check: 'Controlla di nuovo',
    stillPending:
      'Poneva non ha ancora completato questa operazione. Potrebbe essere ancora in corso, quindi non è possibile decidere altro finché non sarà completata.',
    stillUnknown:
      'Non abbiamo ricevuto una risposta. Non è possibile decidere altro finché questa operazione non sarà completata. Riprova tra poco.',
    requestClosed:
      'Questa richiesta è chiusa. Chiedi all’agente di collegarsi di nuovo per avviarne una nuova.',
    recordedOther:
      'È già stata registrata un’altra scelta: {choice}. La tua scelta non è stata applicata.',
    choiceAllow: 'consentito',
    choiceDeny: 'non consentito',
    staleOnSend:
      'Il tuo accesso è cambiato da quando è stata effettuata questa richiesta. Non è stato concesso nulla. Chiedi all’agente di collegarsi di nuovo.',
    refusedOnSend: 'Poneva ha rifiutato questa richiesta. Non è stato concesso nulla.',
    noAccess: 'Nessun accesso',
    lastSeenNever: 'Non ancora utilizzato',
    lastSeenStale: 'Non visto di recente · ultima visualizzazione {when}',
    lastSeen: 'Ultima visualizzazione {when}',
    stateActive: 'Collegato',
    stateRevoked: 'Revocato',
    stateExpired: 'Scaduto',
    needsConsent: 'Richiede ulteriore accesso',
    review: 'Esamina la richiesta',
    revoke: 'Revoca',
    revokeConfirm:
      'Revocare {client}? Smetterà di funzionare immediatamente. Potrai collegarlo di nuovo in seguito.',
    revokeYes: 'Revoca',
    revokeNo: 'Mantieni',
    revokeFailed: 'Non è stato possibile revocarlo. Potrebbe essere ancora connesso. Riprova.',
    revokeDenied: 'Non puoi revocare questo agente.',
    revokeGone: 'Questo agente non esiste più. L’elenco mostra lo stato attuale.',
    yourAgents: 'I tuoi agenti',
    companyAgents: 'Agenti di tutti',
    companyAgentsHelp:
      'In qualità di amministratore dell’azienda, puoi vedere e revocare tutti gli agenti di programmazione.',
  },
  lv: {
    title: 'Kodēšanas aģenti',
    intro:
      'Kodēšanas aģenti, kurus palaižat pats, var lasīt jūsu uzņēmuma zināšanas un strādāt ar lietotnēm. Katrs no tiem pieprasa tikai tieši nepieciešamo piekļuvi, un nekas netiek paplašināts bez jūsu piekrišanas.',
    none: 'Kodēšanas aģenti vēl nav pievienoti.',
    loading: 'Notiek kodēšanas aģentu ielāde…',
    error: 'Neizdevās ielādēt jūsu kodēšanas aģentus. Mēģiniet vēlreiz.',
    consentTitle: 'Atļaut MI aģentam izmantot {company}?',
    notVerified:
      'MI aģents, kas sevi dēvē par “{client}”, vēlas izmantot {company}. Poneva nevar pārbaudīt šo nosaukumu.',
    notVerifiedOrigin:
      'MI aģents, kas vietnē {origin} sevi dēvē par “{client}”, vēlas izmantot {company}. Poneva nevar pārbaudīt šo nosaukumu.',
    dataLine:
      'Tas, ko aģents nolasa, tiek nosūtīts uzņēmumam {vendor} saskaņā ar {person} personīgo plānu, ārpus Poneva ES mitināšanas vides.',
    expiryDays:
      'Piekļuve ir spēkā {days} dienas. Jebkurā laikā varat to atsaukt sadaļā Savienojumi → Kodēšanas aģenti.',
    expiryDay:
      'Piekļuve ir spēkā {days} dienu. Jebkurā laikā varat to atsaukt sadaļā Savienojumi → Kodēšanas aģenti.',
    headingAsking: 'Pieprasītā piekļuve',
    headingRefused: 'Šis pieprasījums tika noraidīts',
    headingConsented: 'Jūs atļāvāt',
    headingRevoked: 'Šī piekļuve tika atsaukta',
    headingStale: 'Šis pieprasījums vairs nav aktuāls',
    availAsked: 'Tiks atļauts',
    availAlready: 'Jau atļauts',
    availNotAvailable: 'Jums nav pieejams',
    notAvailableNote:
      'Jūsu loma uzņēmumā {company} neietver šo piekļuvi, tāpēc tā netiks piešķirta.',
    notePartly:
      'Daļa pieprasītās piekļuves jums nav pieejama. Ja to atļausiet, tiks piešķirta tikai piekļuve, kas atzīmēta kā “Tiks atļauts”.',
    noteBeyond:
      'Neviena no pieprasītajām piekļuvēm jums nav pieejama, tāpēc nav nekā, ko atļaut. Lūdziet uzņēmuma administratoram piešķirt piekļuvi.',
    noteStale:
      'Pēc aģenta pieprasījuma jūsu piekļuve uzņēmumam ir mainījusies. Lūdziet aģentam izveidot savienojumu vēlreiz, lai pārskatītu pašreizējo pieprasījumu.',
    noteRevoked: 'Aģents vairs nevar izmantot šo piekļuvi.',
    noteConsented: 'Jebkurā laikā varat to mainīt vai atsaukt sadaļā Kodēšanas aģenti.',
    refusalReadRequired:
      'Aģents nevar pieprasīt tikai piekļuvi lietotnēm. Tam ir nepieciešama arī piekļuve uzņēmuma zināšanām. Lūdziet aģentam izveidot savienojumu vēlreiz ar abām piekļuvēm.',
    refusalGeneral: 'Pieprasījums tika noraidīts. Lūdziet aģentam izveidot savienojumu vēlreiz.',
    allow: 'Atļaut',
    deny: 'Noraidīt',
    working: 'Notiek darbība…',
    unconfirmed:
      'Neizdevās apstiprināt jūsu izvēli. Iespējams, tā tika vai netika reģistrēta. Pirms izvēlaties vēlreiz, pārbaudiet vēlreiz.',
    check: 'Pārbaudīt vēlreiz',
    stillPending:
      'Poneva vēl nav pabeigusi šo darbību. Tā joprojām var tikt apstrādāta, tāpēc līdz tās pabeigšanai neko citu nevar izlemt.',
    stillUnknown:
      'Neizdevās saņemt atbildi. Kamēr tas nav atrisināts, neko citu nevar izlemt. Pēc brīža mēģiniet vēlreiz.',
    requestClosed:
      'Šis pieprasījums ir slēgts. Lūdziet aģentam izveidot savienojumu vēlreiz, lai sāktu jaunu pieprasījumu.',
    recordedOther: 'Jau tika reģistrēta cita izvēle: {choice}. Jūsu izvēle netika lietota.',
    choiceAllow: 'atļauts',
    choiceDeny: 'nav atļauts',
    staleOnSend:
      'Kopš šī pieprasījuma izveides jūsu piekļuve ir mainījusies. Nekas netika piešķirts. Lūdziet aģentam izveidot savienojumu vēlreiz.',
    refusedOnSend: 'Poneva noraidīja šo pieprasījumu. Nekas netika piešķirts.',
    noAccess: 'Nav piekļuves',
    lastSeenNever: 'Vēl nav izmantots',
    lastSeenStale: 'Ilgāku laiku nav redzēts · pēdējo reizi redzēts {when}',
    lastSeen: 'Pēdējo reizi redzēts {when}',
    stateActive: 'Savienots',
    stateRevoked: 'Atsaukts',
    stateExpired: 'Beidzies',
    needsConsent: 'Pieprasa papildu piekļuvi',
    review: 'Pārskatīt pieprasījumu',
    revoke: 'Atsaukt',
    revokeConfirm:
      'Atsaukt {client}? Tas nekavējoties pārtrauks darboties. Vēlāk varēsiet to atkal savienot.',
    revokeYes: 'Atsaukt',
    revokeNo: 'Paturēt',
    revokeFailed: 'Neizdevās to atsaukt. Tas joprojām var būt savienots. Mēģiniet vēlreiz.',
    revokeDenied: 'Jūs nevarat atsaukt šo aģentu.',
    revokeGone: 'Šis aģents jau ir noņemts. Sarakstā redzams pašreizējais stāvoklis.',
    yourAgents: 'Jūsu aģenti',
    companyAgents: 'Visu lietotāju aģenti',
    companyAgentsHelp:
      'Kā uzņēmuma administrators varat skatīt un atsaukt jebkuru kodēšanas aģentu.',
  },
  lt: {
    title: 'Kodavimo agentai',
    intro:
      'Jūsų paleidžiami kodavimo agentai gali skaityti jūsų įmonės žinias ir dirbti su programėlėmis. Kiekvienas jų prašo tiksliai tokios prieigos, kurios jam reikia, ir niekas neplečiama be jūsų sutikimo.',
    none: 'Kodavimo agentai dar neprijungti.',
    loading: 'Įkeliami kodavimo agentai…',
    error: 'Nepavyko įkelti jūsų kodavimo agentų. Bandykite dar kartą.',
    consentTitle: 'Leisti DI agentui naudoti {company}?',
    notVerified:
      'DI agentas, prisistatantis „{client}“, nori naudoti {company}. Poneva negali patvirtinti šio pavadinimo.',
    notVerifiedOrigin:
      'DI agentas, prisistatantis „{client}“ svetainėje {origin}, nori naudoti {company}. Poneva negali patvirtinti šio pavadinimo.',
    dataLine:
      'Tai, ką jis perskaito, perduodama {vendor} pagal paties {person} planą, ne Poneva ES prieglobos aplinkoje.',
    expiryDays:
      'Prieiga galioja {days} dienas. Bet kada galite ją atšaukti skiltyje Ryšiai → Kodavimo agentai.',
    expiryDay:
      'Prieiga galioja {days} dieną. Bet kada galite ją atšaukti skiltyje Ryšiai → Kodavimo agentai.',
    headingAsking: 'Prašoma suteikti',
    headingRefused: 'Šis prašymas atmestas',
    headingConsented: 'Leidote',
    headingRevoked: 'Ši prieiga atšaukta',
    headingStale: 'Šis prašymas nebegalioja',
    availAsked: 'Bus leista',
    availAlready: 'Jau leista',
    availNotAvailable: 'Jums nepasiekiama',
    notAvailableNote: 'Jūsų vaidmuo įmonėje {company} to neapima, todėl prieiga nebus suteikta.',
    notePartly:
      'Dalis prašomos prieigos jums nepasiekiama. Jei leisite, bus suteikta tik prieiga, pažymėta „Bus leista“.',
    noteBeyond:
      'Nė viena prašomos prieigos dalis jums nepasiekiama, todėl nėra ko leisti. Kreipkitės į įmonės administratorių.',
    noteStale:
      'Jūsų prieiga prie įmonės pasikeitė agentui pateikus prašymą. Paprašykite agento prisijungti iš naujo, kad galėtumėte peržiūrėti dabartinį prašymą.',
    noteRevoked: 'Agentas nebegali naudoti šios prieigos.',
    noteConsented: 'Bet kada galite tai pakeisti arba atšaukti skiltyje Kodavimo agentai.',
    refusalReadRequired:
      'Agentas negali prašyti vien programėlės prieigos. Jam taip pat reikia skaityti įmonės žinias. Paprašykite agento prisijungti iš naujo ir prašyti abiejų prieigų.',
    refusalGeneral: 'Prašymas atmestas. Paprašykite agento prisijungti iš naujo.',
    allow: 'Leisti',
    deny: 'Atšaukti',
    working: 'Vykdoma…',
    unconfirmed:
      'Nepavyko patvirtinti jūsų pasirinkimo. Jis galėjo būti įrašytas arba ne. Prieš rinkdamiesi dar kartą, patikrinkite.',
    check: 'Patikrinti dar kartą',
    stillPending:
      'Poneva dar neapdorojo šio prašymo. Jis vis dar gali būti vykdomas, todėl kol kas negalima priimti jokių kitų sprendimų.',
    stillUnknown:
      'Nepavyko gauti atsakymo. Kol tai nebus išspręsta, negalima priimti jokių kitų sprendimų. Po akimirkos bandykite dar kartą.',
    requestClosed:
      'Šis prašymas uždarytas. Paprašykite agento prisijungti iš naujo, kad pradėtumėte naują prašymą.',
    recordedOther: 'Jau įrašytas kitas pasirinkimas: {choice}. Jūsų pasirinkimas nepritaikytas.',
    choiceAllow: 'leista',
    choiceDeny: 'neleista',
    staleOnSend:
      'Jūsų prieiga pasikeitė pateikus šį prašymą. Niekas nesuteikta. Paprašykite agento prisijungti iš naujo.',
    refusedOnSend: 'Poneva atmetė šį prašymą. Nieko nesuteikta.',
    noAccess: 'Prieigos nėra',
    lastSeenNever: 'Dar nenaudota',
    lastSeenStale: 'Neseniai nematyta · paskutinį kartą matyta {when}',
    lastSeen: 'Paskutinį kartą matyta {when}',
    stateActive: 'Prijungta',
    stateRevoked: 'Atšaukta',
    stateExpired: 'Baigė galioti',
    needsConsent: 'Prašoma papildomos prieigos',
    review: 'Peržiūrėti prašymą',
    revoke: 'Atšaukti',
    revokeConfirm: 'Atšaukti {client}? Jis iškart nustos veikti. Vėliau galėsite jį vėl prijungti.',
    revokeYes: 'Atšaukti',
    revokeNo: 'Palikti',
    revokeFailed: 'Nepavyko atšaukti. Ryšys vis dar gali būti aktyvus. Bandykite dar kartą.',
    revokeDenied: 'Negalite atšaukti šio agento.',
    revokeGone: 'Šio agento nebėra. Sąraše rodoma dabartinė būsena.',
    yourAgents: 'Jūsų agentai',
    companyAgents: 'Visų agentai',
    companyAgentsHelp:
      'Kaip įmonės administratorius galite peržiūrėti ir atšaukti visų programavimo agentų prieigą.',
  },
  mt: {
    title: 'Aġenti tal-kodifikazzjoni',
    intro:
      'L-aġenti tal-kodifikazzjoni li tħaddem int stess jistgħu jaqraw l-għarfien tal-kumpanija tiegħek u jaħdmu mal-applikazzjonijiet. Kull wieħed jitlob eżattament l-aċċess li jeħtieġ, u xejn ma jiżdied mingħajr il-kunsens tiegħek.',
    none: 'Għad m’hemmx aġenti tal-kodifikazzjoni konnessi.',
    loading: 'Qed jitgħabbew l-aġenti tal-kodifikazzjoni…',
    error: 'Ma stajniex nitgħabbew l-aġenti tal-kodifikazzjoni tiegħek. Erġa’ pprova.',
    consentTitle: 'Tippermetti lil aġent tal-AI juża {company}?',
    notVerified:
      'Aġent tal-AI li jsejjaħ lilu nnifsu “{client}” irid juża {company}. Poneva ma tistax tivverifika l-isem.',
    notVerifiedOrigin:
      'Aġent tal-AI li jsejjaħ lilu nnifsu “{client}” fuq {origin} irid juża {company}. Poneva ma tistax tivverifika l-isem.',
    dataLine:
      'Dak li jaqra jintbagħat lil {vendor} taħt il-pjan personali ta’ {person}, barra mill-hosting tal-UE ta’ Poneva.',
    expiryDays:
      'L-aċċess idum {days} jum. Tista’ tirrevokah fi kwalunkwe ħin minn Konnessjonijiet → Aġenti tal-kodifikazzjoni.',
    expiryDay:
      'L-aċċess idum {days} jum. Tista’ tirrevokah fi kwalunkwe ħin minn Konnessjonijiet → Aġenti tal-kodifikazzjoni.',
    headingAsking: 'Jitlob li',
    headingRefused: 'Din it-talba ġiet irrifjutata',
    headingConsented: 'Int ippermettejt',
    headingRevoked: 'Dan l-aċċess ġie rrevokat',
    headingStale: 'Din it-talba m’għadhiex aġġornata',
    availAsked: 'Se jiġi permess',
    availAlready: 'Diġà permess',
    availNotAvailable: 'Mhux disponibbli għalik',
    notAvailableNote: 'Ir-rwol tiegħek f’{company} ma jinkludix dan, għalhekk mhux se jingħata.',
    notePartly:
      'Parti minn dak li talab mhijiex disponibbli għalik. Jekk tippermettih, jingħata biss l-aċċess immarkat “Se jiġi permess”.',
    noteBeyond:
      'Xejn minn dak li talab mhuwiex disponibbli għalik, għalhekk m’hemm xejn x’jiġi permess. Staqsi lil amministratur tal-kumpanija.',
    noteStale:
      'L-aċċess tal-kumpanija tiegħek inbidel wara li l-aġent għamel it-talba. Staqsi lill-aġent jerġa’ jikkonnettja biex tirrevedi t-talba attwali.',
    noteRevoked: 'L-aġent ma jistax jibqa’ juża dan l-aċċess.',
    noteConsented:
      'Tista’ tibdel jew tirrevoka dan fi kwalunkwe ħin taħt Aġenti tal-kodifikazzjoni.',
    refusalReadRequired:
      'Aġent ma jistax jitlob aċċess għall-applikazzjonijiet waħdu. Jeħtieġ ukoll jaqra l-għarfien tal-kumpanija. Staqsi lill-aġent jerġa’ jikkonnettja bit-tnejn li huma.',
    refusalGeneral: 'It-talba ġiet irrifjutata. Staqsi lill-aġent jerġa’ jikkonnettja.',
    allow: 'Ħalli',
    deny: 'Ikkanċella',
    working: 'Qed jaħdem…',
    unconfirmed:
      'Ma stajniex nikkonfermaw l-għażla tiegħek. Jista’ jkun li ġiet irreġistrata jew le. Iċċekkja mill-ġdid qabel terġa’ tagħżel.',
    check: 'Iċċekkja mill-ġdid',
    stillPending:
      'Poneva għadha ma ffinalizzatx dan. Jista’ jkun li għadu għaddej, għalhekk ma tista’ tittieħed l-ebda deċiżjoni oħra sakemm jiġi ffinalizzat.',
    stillUnknown:
      'Ma stajniex niksbu tweġiba. Ma tista’ tittieħed l-ebda deċiżjoni oħra sakemm dan jiġi ffinalizzat. Erġa’ pprova wara ftit.',
    requestClosed:
      'Din it-talba ngħalqet. Staqsi lill-aġent jerġa’ jikkonnettja biex jibda waħda ġdida.',
    recordedOther:
      'Diġà ġiet irreġistrata għażla oħra: {choice}. L-għażla tiegħek ma ġietx applikata.',
    choiceAllow: 'permess',
    choiceDeny: 'mhux permess',
    staleOnSend:
      'L-aċċess tiegħek inbidel minn meta saret din it-talba. Ma ngħata xejn. Staqsi lill-aġent jerġa’ jikkonnettja.',
    refusedOnSend: 'Poneva rrifjutat din it-talba. Ma ngħata xejn.',
    noAccess: 'L-ebda aċċess',
    lastSeenNever: 'Għadu ma ntużax',
    lastSeenStale: 'Ma deherx dan l-aħħar · deher l-aħħar {when}',
    lastSeen: 'Deher l-aħħar {when}',
    stateActive: 'Konness',
    stateRevoked: 'Irrivokat',
    stateExpired: 'Skada',
    needsConsent: 'Jitlob aktar aċċess',
    review: 'Irrevedi t-talba',
    revoke: 'Irrevoka',
    revokeConfirm:
      'Tirrevoka {client}? Jieqaf jaħdem mill-ewwel. Tista’ terġa’ tikkonnettjah aktar tard.',
    revokeYes: 'Revoka',
    revokeNo: 'Żomm',
    revokeFailed: "Ma stajniex nirrevokawh. Jista' jkun għadu konness. Erġa' pprova.",
    revokeDenied: 'Ma tistax tirrevoka dan l-aġent.',
    revokeGone: 'Dan l-aġent diġà spiċċa. Il-lista turi l-istat attwali.',
    yourAgents: 'L-aġenti tiegħek',
    companyAgents: 'L-aġenti tal-kumpanija',
    companyAgentsHelp:
      "Bħala amministratur tal-kumpanija tista' tara u tirrevoka kull aġent tal-kodifikazzjoni.",
  },
  pl: {
    title: 'Agenci kodujący',
    intro:
      'Agenci kodujący uruchamiani przez Ciebie mogą czytać firmową bazę wiedzy i współpracować z aplikacjami. Każdy z nich prosi dokładnie o potrzebny dostęp i nic nie zostanie rozszerzone bez Twojej zgody.',
    none: 'Nie połączono jeszcze żadnych agentów kodujących.',
    loading: 'Ładowanie agentów kodujących…',
    error: 'Nie udało się załadować Twoich agentów kodujących. Spróbuj ponownie.',
    consentTitle: 'Zezwolić agentowi AI na używanie {company}?',
    notVerified:
      'Agent AI przedstawiający się jako „{client}” chce używać {company}. Poneva nie może zweryfikować tej nazwy.',
    notVerifiedOrigin:
      'Agent AI przedstawiający się jako „{client}” w domenie {origin} chce używać {company}. Poneva nie może zweryfikować tej nazwy.',
    dataLine:
      'Odczytywane dane trafią do {vendor} w ramach indywidualnego planu użytkownika {person}, poza hostingiem Poneva w UE.',
    expiryDays:
      'Dostęp będzie ważny przez {days} dni. Możesz go w każdej chwili cofnąć w sekcji Połączenia → Agenci kodujący.',
    expiryDay:
      'Dostęp będzie ważny przez {days} dzień. Możesz go w każdej chwili cofnąć w sekcji Połączenia → Agenci kodujący.',
    headingAsking: 'Prosi o',
    headingRefused: 'Ta prośba została odrzucona',
    headingConsented: 'Udzielono zgody',
    headingRevoked: 'Ten dostęp został cofnięty',
    headingStale: 'Ta prośba jest nieaktualna',
    availAsked: 'Zostanie udzielony',
    availAlready: 'Już udzielony',
    availNotAvailable: 'Niedostępny dla Ciebie',
    notAvailableNote:
      'Twoja rola w firmie {company} nie obejmuje tego dostępu, więc nie zostanie on udzielony.',
    notePartly:
      'Część żądanego dostępu nie jest dla Ciebie dostępna. Jeśli zezwolisz, zostanie udzielony tylko dostęp oznaczony jako „Zostanie udzielony”.',
    noteBeyond:
      'Żaden z żądanych dostępów nie jest dla Ciebie dostępny, więc nie ma na co zezwolić. Poproś administratora firmy.',
    noteStale:
      'Twój dostęp do firmy zmienił się po wysłaniu prośby przez agenta. Poproś agenta o ponowne połączenie, aby przejrzeć aktualną prośbę.',
    noteRevoked: 'Agent nie może już korzystać z tego dostępu.',
    noteConsented: 'Możesz to zmienić lub cofnąć w dowolnym momencie w sekcji Agenci kodujący.',
    refusalReadRequired:
      'Agent nie może prosić wyłącznie o dostęp do aplikacji. Potrzebuje również możliwości odczytu firmowej bazy wiedzy. Poproś agenta o ponowne połączenie z oboma uprawnieniami.',
    refusalGeneral: 'Prośba została odrzucona. Poproś agenta o ponowne połączenie.',
    allow: 'Zezwól',
    deny: 'Odmów',
    working: 'Przetwarzanie…',
    unconfirmed:
      'Nie udało się potwierdzić Twojego wyboru. Mógł on zostać zapisany, ale nie jest to pewne. Sprawdź ponownie przed dokonaniem kolejnego wyboru.',
    check: 'Sprawdź ponownie',
    stillPending:
      'Poneva nie rozstrzygnęła jeszcze tej prośby. Może być nadal przetwarzana, dlatego do czasu jej rozstrzygnięcia nie można podjąć żadnej innej decyzji.',
    stillUnknown:
      'Nie udało się uzyskać odpowiedzi. Do czasu rozstrzygnięcia tej prośby nie można podjąć żadnej innej decyzji. Spróbuj ponownie za chwilę.',
    requestClosed:
      'Ta prośba jest zamknięta. Poproś agenta o ponowne połączenie, aby rozpocząć nową.',
    recordedOther: 'Zapisano już inny wybór: {choice}. Twój wybór nie został zastosowany.',
    choiceAllow: 'zezwolono',
    choiceDeny: 'nie zezwolono',
    staleOnSend:
      'Twój dostęp zmienił się od czasu utworzenia tej prośby. Nie udzielono żadnych uprawnień. Poproś agenta o ponowne połączenie.',
    refusedOnSend: 'Poneva odrzuciła tę prośbę. Nie udzielono żadnych uprawnień.',
    noAccess: 'Brak dostępu',
    lastSeenNever: 'Jeszcze nieużywany',
    lastSeenStale: 'Niewidziany od pewnego czasu · ostatnio {when}',
    lastSeen: 'Ostatnio widziany: {when}',
    stateActive: 'Połączony',
    stateRevoked: 'Cofnięto',
    stateExpired: 'Wygasł',
    needsConsent: 'Prosi o dodatkowy dostęp',
    review: 'Przejrzyj prośbę',
    revoke: 'Cofnij',
    revokeConfirm:
      'Cofnąć dostęp dla {client}? Agent przestanie od razu działać. Możesz połączyć go ponownie później.',
    revokeYes: 'Cofnij',
    revokeNo: 'Zachowaj',
    revokeFailed: 'Nie udało się tego cofnąć. Agent może być nadal połączony. Spróbuj ponownie.',
    revokeDenied: 'Nie możesz cofnąć tego agenta.',
    revokeGone: 'Tego agenta już nie ma. Lista pokazuje aktualny stan.',
    yourAgents: 'Twoje agenty',
    companyAgents: 'Agenty wszystkich',
    companyAgentsHelp:
      'Jako administrator firmy możesz wyświetlać i cofać dostęp każdego agenta kodującego.',
  },
  pt: {
    title: 'Agentes de programação',
    intro:
      'Os agentes de programação que você executa por conta própria podem ler o conhecimento da sua empresa e trabalhar com aplicativos. Cada um solicita exatamente o acesso de que precisa, e nada é ampliado sem a sua autorização.',
    none: 'Ainda não há agentes de programação conectados.',
    loading: 'Carregando agentes de programação…',
    error: 'Não foi possível carregar seus agentes de programação. Tente novamente.',
    consentTitle: 'Permitir que um agente de IA use {company}?',
    notVerified:
      'Um agente de IA que se identifica como “{client}” quer usar {company}. O Poneva não consegue verificar esse nome.',
    notVerifiedOrigin:
      'Um agente de IA que se identifica como “{client}” em {origin} quer usar {company}. O Poneva não consegue verificar esse nome.',
    dataLine:
      'O que ele ler será enviado para {vendor} pelo plano da própria pessoa {person}, fora da hospedagem europeia do Poneva.',
    expiryDays:
      'O acesso dura {days} dias. Você pode revogá-lo a qualquer momento em Conexões → Agentes de programação.',
    expiryDay:
      'O acesso dura {days} dia. Você pode revogá-lo a qualquer momento em Conexões → Agentes de programação.',
    headingAsking: 'Solicita',
    headingRefused: 'Esta solicitação foi recusada',
    headingConsented: 'Você permitiu',
    headingRevoked: 'Este acesso foi revogado',
    headingStale: 'Esta solicitação está desatualizada',
    availAsked: 'Será permitido',
    availAlready: 'Já permitido',
    availNotAvailable: 'Indisponível para você',
    notAvailableNote: 'Sua função em {company} não inclui isso, portanto não será concedido.',
    notePartly:
      'Parte do que foi solicitado não está disponível para você. Se você permitir, somente o acesso marcado como “Será permitido” será concedido.',
    noteBeyond:
      'Nada do que foi solicitado está disponível para você, portanto não há nada a permitir. Fale com um administrador da empresa.',
    noteStale:
      'Seu acesso à empresa mudou depois que o agente fez a solicitação. Peça ao agente para se conectar novamente para que você analise a solicitação atual.',
    noteRevoked: 'O agente não pode mais usar este acesso.',
    noteConsented:
      'Você pode alterar ou revogar isso a qualquer momento em Agentes de programação.',
    refusalReadRequired:
      'Um agente não pode solicitar apenas acesso a aplicativos. Ele também precisa ler o conhecimento da empresa. Peça ao agente para se conectar novamente com as duas permissões.',
    refusalGeneral: 'A solicitação foi recusada. Peça ao agente para se conectar novamente.',
    allow: 'Permitir',
    deny: 'Recusar',
    working: 'Processando…',
    unconfirmed:
      'Não foi possível confirmar sua escolha. Ela pode ou não ter sido registrada. Verifique novamente antes de escolher outra vez.',
    check: 'Verificar novamente',
    stillPending:
      'O Poneva ainda não concluiu isso. O processo pode estar em andamento, então nada mais pode ser decidido até que seja concluído.',
    stillUnknown:
      'Não foi possível obter uma resposta. Nada mais pode ser decidido até que isso seja concluído. Tente novamente daqui a pouco.',
    requestClosed:
      'Esta solicitação foi encerrada. Peça ao agente para se conectar novamente e iniciar uma nova solicitação.',
    recordedOther: 'Outra escolha já foi registrada: {choice}. Sua escolha não foi aplicada.',
    choiceAllow: 'permitido',
    choiceDeny: 'não permitido',
    staleOnSend:
      'Seu acesso mudou desde que esta solicitação foi feita. Nada foi concedido. Peça ao agente para se conectar novamente.',
    refusedOnSend: 'O Poneva recusou esta solicitação. Nada foi concedido.',
    noAccess: 'Sem acesso',
    lastSeenNever: 'Ainda não usado',
    lastSeenStale: 'Não visto recentemente · visto pela última vez em {when}',
    lastSeen: 'Visto pela última vez em {when}',
    stateActive: 'Conectado',
    stateRevoked: 'Revogado',
    stateExpired: 'Expirado',
    needsConsent: 'Solicitando mais acesso',
    review: 'Analisar solicitação',
    revoke: 'Revogar',
    revokeConfirm:
      'Revogar {client}? Ele deixará de funcionar imediatamente. Você poderá conectá-lo novamente mais tarde.',
    revokeYes: 'Revogar',
    revokeNo: 'Manter',
    revokeFailed: 'Não foi possível revogar. Ele pode continuar conectado. Tente novamente.',
    revokeDenied: 'Você não pode revogar este agente.',
    revokeGone: 'Este agente já não existe. A lista mostra o estado atual.',
    yourAgents: 'Seus agentes',
    companyAgents: 'Agentes de todos',
    companyAgentsHelp:
      'Como administrador da empresa, você pode ver e revogar todos os agentes de programação.',
  },
  ro: {
    title: 'Agenți de programare',
    intro:
      'Agenții de programare pe care îi rulezi singur pot citi informațiile companiei tale și pot lucra cu aplicații. Fiecare solicită exact accesul de care are nevoie și nimic nu se extinde fără acordul tău.',
    none: 'Nu este conectat încă niciun agent de programare.',
    loading: 'Se încarcă agenții de programare…',
    error: 'Nu am putut încărca agenții tăi de programare. Încearcă din nou.',
    consentTitle: 'Permiți unui agent AI să folosească {company}?',
    notVerified:
      'Un agent AI care se identifică drept „{client}” vrea să folosească {company}. Poneva nu poate verifica acest nume.',
    notVerifiedOrigin:
      'Un agent AI care se identifică drept „{client}” pe {origin} vrea să folosească {company}. Poneva nu poate verifica acest nume.',
    dataLine:
      'Datele citite sunt trimise către {vendor} în cadrul planului propriu al lui {person}, în afara găzduirii Poneva din UE.',
    expiryDays:
      'Accesul este valabil {days} zile. Îl poți revoca oricând din Conexiuni → Agenți de programare.',
    expiryDay:
      'Accesul este valabil {days} zi. Îl poți revoca oricând din Conexiuni → Agenți de programare.',
    headingAsking: 'Solicită',
    headingRefused: 'Această solicitare a fost refuzată',
    headingConsented: 'Ai permis',
    headingRevoked: 'Acest acces a fost revocat',
    headingStale: 'Această solicitare nu mai este actuală',
    availAsked: 'Va fi permis',
    availAlready: 'Deja permis',
    availNotAvailable: 'Nu îți este disponibil',
    notAvailableNote: 'Rolul tău în {company} nu include acest acces, așa că nu va fi acordat.',
    notePartly:
      'O parte din accesul solicitat nu îți este disponibilă. Dacă îl permiți, va fi acordat doar accesul marcat „Va fi permis”.',
    noteBeyond:
      'Niciunul dintre accesele solicitate nu îți este disponibil, așa că nu este nimic de permis. Contactează un administrator al companiei.',
    noteStale:
      'Accesul tău la companie s-a schimbat după solicitarea agentului. Cere agentului să se conecteze din nou pentru a verifica solicitarea actuală.',
    noteRevoked: 'Agentul nu mai poate folosi acest acces.',
    noteConsented: 'Poți modifica sau revoca acest acces oricând din Agenți de programare.',
    refusalReadRequired:
      'Un agent nu poate solicita doar acces la aplicații. De asemenea, trebuie să poată citi informațiile companiei. Cere agentului să se conecteze din nou cu ambele permisiuni.',
    refusalGeneral: 'Solicitarea a fost refuzată. Cere agentului să se conecteze din nou.',
    allow: 'Permite',
    deny: 'Anulează',
    working: 'Se procesează…',
    unconfirmed:
      'Nu am putut confirma alegerea ta. Este posibil să fi fost înregistrată sau nu. Verifică din nou înainte de a alege încă o dată.',
    check: 'Verifică din nou',
    stillPending:
      'Poneva nu a finalizat încă această operațiune. Este posibil să fie încă în curs, așa că nu se poate lua nicio altă decizie până la finalizare.',
    stillUnknown:
      'Nu am putut primi un răspuns. Nu se poate lua nicio altă decizie până la finalizare. Încearcă din nou peste câteva momente.',
    requestClosed:
      'Această solicitare este închisă. Cere agentului să se conecteze din nou pentru a începe una nouă.',
    recordedOther:
      'A fost deja înregistrată o altă alegere: {choice}. Alegerea ta nu a fost aplicată.',
    choiceAllow: 'permis',
    choiceDeny: 'nepermis',
    staleOnSend:
      'Accesul tău s-a schimbat de când a fost creată această solicitare. Nu a fost acordat niciun acces. Cere agentului să se conecteze din nou.',
    refusedOnSend: 'Poneva a refuzat această solicitare. Nu a fost acordat niciun acces.',
    noAccess: 'Fără acces',
    lastSeenNever: 'Nu a fost folosit încă',
    lastSeenStale: 'Nu a fost văzut recent · ultima dată {when}',
    lastSeen: 'Văzut ultima dată {when}',
    stateActive: 'Conectat',
    stateRevoked: 'Revocat',
    stateExpired: 'Expirat',
    needsConsent: 'Solicită mai mult acces',
    review: 'Examinează solicitarea',
    revoke: 'Revocă',
    revokeConfirm:
      'Revoci accesul pentru {client}? Va înceta să funcționeze imediat. Îl poți conecta din nou mai târziu.',
    revokeYes: 'Revocă',
    revokeNo: 'Păstrează',
    revokeFailed: 'Nu am putut să-l revocăm. Este posibil să fie încă conectat. Încearcă din nou.',
    revokeDenied: 'Nu poți revoca acest agent.',
    revokeGone: 'Acest agent nu mai există. Lista afișează starea actuală.',
    yourAgents: 'Agenții tăi',
    companyAgents: 'Agenții tuturor',
    companyAgentsHelp:
      'În calitate de administrator al companiei, poți vedea și revoca orice agent de programare.',
  },
  sk: {
    title: 'Kódovací agenti',
    intro:
      'Kódovací agenti, ktorých spúšťate sami, môžu čítať znalosti vašej spoločnosti a pracovať s aplikáciami. Každý si vyžiada presne potrebný prístup a nič sa nerozšíri bez vášho súhlasu.',
    none: 'Zatiaľ nie sú pripojení žiadni kódovací agenti.',
    loading: 'Načítavajú sa kódovací agenti…',
    error: 'Kódovacích agentov sa nepodarilo načítať. Skúste znova.',
    consentTitle: 'Povoliť AI agentovi používať {company}?',
    notVerified:
      'AI agent, ktorý sa označuje ako „{client}“, chce používať {company}. Poneva nemôže overiť tento názov.',
    notVerifiedOrigin:
      'AI agent, ktorý sa na {origin} označuje ako „{client}“, chce používať {company}. Poneva nemôže overiť tento názov.',
    dataLine:
      'To, čo číta, odosiela do {vendor} v rámci vlastného programu používateľa {person}, mimo hostingu Poneva v EÚ.',
    expiryDays:
      'Prístup trvá {days} dní. Kedykoľvek ho môžete odvolať v časti Pripojenia → Kódovací agenti.',
    expiryDay:
      'Prístup trvá {days} deň. Kedykoľvek ho môžete odvolať v časti Pripojenia → Kódovací agenti.',
    headingAsking: 'Požaduje',
    headingRefused: 'Táto žiadosť bola zamietnutá',
    headingConsented: 'Povolili ste',
    headingRevoked: 'Tento prístup bol odvolaný',
    headingStale: 'Táto žiadosť je neaktuálna',
    availAsked: 'Bude povolené',
    availAlready: 'Už povolené',
    availNotAvailable: 'Nie je vám k dispozícii',
    notAvailableNote: 'Vaša rola v spoločnosti {company} toto nezahŕňa, preto sa to nepovolí.',
    notePartly:
      'Niektoré požadované oprávnenia vám nie sú k dispozícii. Ak to povolíte, udelí sa iba prístup označený ako „Bude povolené“.',
    noteBeyond:
      'Žiadne z požadovaných oprávnení vám nie je k dispozícii, takže nie je čo povoliť. Obráťte sa na správcu spoločnosti.',
    noteStale:
      'Váš prístup k spoločnosti sa po odoslaní žiadosti zmenil. Požiadajte agenta, aby sa pripojil znova, a mohli ste skontrolovať aktuálnu žiadosť.',
    noteRevoked: 'Agent už tento prístup nemôže používať.',
    noteConsented: 'Toto môžete kedykoľvek zmeniť alebo odvolať v časti Kódovací agenti.',
    refusalReadRequired:
      'Agent nemôže požiadať iba o prístup k aplikáciám. Potrebuje tiež čítať firemné znalosti. Požiadajte agenta, aby sa znova pripojil s oboma oprávneniami.',
    refusalGeneral: 'Žiadosť bola zamietnutá. Požiadajte agenta, aby sa pripojil znova.',
    allow: 'Povoliť',
    deny: 'Zrušiť',
    working: 'Spracúva sa…',
    unconfirmed:
      'Vašu voľbu sa nepodarilo potvrdiť. Možno sa zaznamenala, možno nie. Pred ďalšou voľbou to znova skontrolujte.',
    check: 'Skontrolovať znova',
    stillPending:
      'Poneva túto žiadosť zatiaľ nedokončila. Stále sa môže spracúvať, takže kým sa nedokončí, nemožno rozhodnúť o ničom ďalšom.',
    stillUnknown:
      'Nepodarilo sa získať odpoveď. Kým sa táto žiadosť nedokončí, nemožno rozhodnúť o ničom ďalšom. O chvíľu skúste znova.',
    requestClosed:
      'Táto žiadosť je uzavretá. Požiadajte agenta, aby sa znova pripojil a začal novú žiadosť.',
    recordedOther: 'Už bola zaznamenaná iná voľba: {choice}. Vaša voľba sa nepoužila.',
    choiceAllow: 'povolené',
    choiceDeny: 'nepovolené',
    staleOnSend:
      'Váš prístup sa od vytvorenia tejto žiadosti zmenil. Nič sa nepovolilo. Požiadajte agenta, aby sa pripojil znova.',
    refusedOnSend: 'Poneva túto žiadosť zamietla. Nič sa nepovolilo.',
    noAccess: 'Bez prístupu',
    lastSeenNever: 'Zatiaľ nepoužité',
    lastSeenStale: 'Dlho neaktívne · naposledy videné {when}',
    lastSeen: 'Naposledy videné {when}',
    stateActive: 'Pripojené',
    stateRevoked: 'Odvolané',
    stateExpired: 'Platnosť vypršala',
    needsConsent: 'Žiada o ďalší prístup',
    review: 'Skontrolovať žiadosť',
    revoke: 'Odvolať',
    revokeConfirm:
      'Odvolať {client}? Okamžite to prestane fungovať. Neskôr ho môžete znova pripojiť.',
    revokeYes: 'Odvolať',
    revokeNo: 'Ponechať',
    revokeFailed: 'Nepodarilo sa nám to odvolať. Agent môže byť stále pripojený. Skúste to znova.',
    revokeDenied: 'Tento agent nemôžete odvolať.',
    revokeGone: 'Tento agent už neexistuje. Zoznam zobrazuje aktuálny stav.',
    yourAgents: 'Vaši agenti',
    companyAgents: 'Agenti všetkých',
    companyAgentsHelp:
      'Ako správca spoločnosti môžete zobraziť a odvolať každého kódovacieho agenta.',
  },
  sl: {
    title: 'Programerski agenti',
    intro:
      'Programerski agenti, ki jih zaženete sami, lahko berejo znanje vašega podjetja in delajo z aplikacijami. Vsak zahteva natanko tiste dostope, ki jih potrebuje, in noben dostop se ne razširi brez vaše odobritve.',
    none: 'Programerski agenti še niso povezani.',
    loading: 'Nalaganje programerskih agentov…',
    error: 'Programerskih agentov ni bilo mogoče naložiti. Poskusite znova.',
    consentTitle: 'Dovolite agentu UI uporabo podjetja {company}?',
    notVerified:
      'Agent UI z imenom »{client}« želi uporabljati {company}. Poneva imena ne more preveriti.',
    notVerifiedOrigin:
      'Agent UI z imenom »{client}« na {origin} želi uporabljati {company}. Poneva imena ne more preveriti.',
    dataLine:
      'Prebrani podatki bodo poslani ponudniku {vendor} v okviru lastnega paketa osebe {person}, zunaj gostovanja Poneva v EU.',
    expiryDays:
      'Dostop velja {days} dni. Kadar koli ga lahko prekličete v razdelku Povezave → Programerski agenti.',
    expiryDay:
      'Dostop velja {days} dan. Kadar koli ga lahko prekličete v razdelku Povezave → Programerski agenti.',
    headingAsking: 'Zahteva',
    headingRefused: 'Ta zahteva je bila zavrnjena',
    headingConsented: 'Dovolili ste',
    headingRevoked: 'Ta dostop je bil preklican',
    headingStale: 'Ta zahteva ni več aktualna',
    availAsked: 'Bo dovoljeno',
    availAlready: 'Že dovoljeno',
    availNotAvailable: 'Za vas ni na voljo',
    notAvailableNote: 'Vaša vloga v podjetju {company} tega ne vključuje, zato ne bo odobreno.',
    notePartly:
      'Del zahtevanega za vas ni na voljo. Če dovolite, bo odobren samo dostop z oznako »Bo dovoljeno«.',
    noteBeyond:
      'Nič od zahtevanega za vas ni na voljo, zato ni mogoče ničesar dovoliti. Obrnite se na skrbnika podjetja.',
    noteStale:
      'Vaš dostop do podjetja se je spremenil po zahtevi agenta. Agent naj se znova poveže, da boste lahko pregledali trenutno zahtevo.',
    noteRevoked: 'Agent tega dostopa ne more več uporabljati.',
    noteConsented: 'To lahko kadar koli spremenite ali prekličete v razdelku Programerski agenti.',
    refusalReadRequired:
      'Agent ne more zahtevati samo dostopa do aplikacij. Potrebuje tudi dostop za branje znanja podjetja. Agent naj se znova poveže z obema dovoljenjema.',
    refusalGeneral: 'Zahteva je bila zavrnjena. Agent naj se znova poveže.',
    allow: 'Dovoli',
    deny: 'Prekliči',
    working: 'Delovanje…',
    unconfirmed:
      'Vaše izbire ni bilo mogoče potrditi. Morda je bila zabeležena, morda ne. Pred ponovno izbiro preverite stanje.',
    check: 'Preveri znova',
    stillPending:
      'Poneva tega še ni zaključila. Morda je še v obdelavi, zato do zaključka ni mogoče sprejeti nobene druge odločitve.',
    stillUnknown:
      'Odgovora ni bilo mogoče pridobiti. Dokler se to ne razreši, ni mogoče sprejeti nobene druge odločitve. Čez trenutek poskusite znova.',
    requestClosed: 'Ta zahteva je zaprta. Agent naj se znova poveže, da začne novo zahtevo.',
    recordedOther:
      'Druga izbira je bila že zabeležena: {choice}. Vaša izbira ni bila uveljavljena.',
    choiceAllow: 'dovoljeno',
    choiceDeny: 'ni dovoljeno',
    staleOnSend:
      'Vaš dostop se je od vložitve te zahteve spremenil. Nič ni bilo odobreno. Agent naj se znova poveže.',
    refusedOnSend: 'Poneva je to zahtevo zavrnila. Nič ni bilo odobreno.',
    noAccess: 'Brez dostopa',
    lastSeenNever: 'Še ni uporabljeno',
    lastSeenStale: 'Pred kratkim ni bilo zaznano · nazadnje zaznano {when}',
    lastSeen: 'Nazadnje zaznano {when}',
    stateActive: 'Povezano',
    stateRevoked: 'Preklicano',
    stateExpired: 'Poteklo',
    needsConsent: 'Zahteva več dostopa',
    review: 'Preglej zahtevo',
    revoke: 'Prekliči',
    revokeConfirm:
      'Prekličete {client}? Tako bo takoj prenehal delovati. Pozneje ga lahko znova povežete.',
    revokeYes: 'Prekliči',
    revokeNo: 'Obdrži',
    revokeFailed: 'Dostopa ni bilo mogoče preklicati. Morda je še vedno povezan. Poskusite znova.',
    revokeDenied: 'Dostopa do tega agenta ne morete preklicati.',
    revokeGone: 'Tega agenta ni več. Seznam prikazuje trenutno stanje.',
    yourAgents: 'Vaši agenti',
    companyAgents: 'Agenti vseh',
    companyAgentsHelp:
      'Kot skrbnik podjetja lahko vidite in prekličete dostop do vseh kodirnih agentov.',
  },
  es: {
    title: 'Agentes de código',
    intro:
      'Los agentes de código que ejecutas tú pueden leer los conocimientos de tu empresa y trabajar con aplicaciones. Cada uno solicita exactamente el acceso que necesita, y nada aumenta sin tu autorización.',
    none: 'Aún no hay agentes de código conectados.',
    loading: 'Cargando agentes de código…',
    error: 'No hemos podido cargar tus agentes de código. Inténtalo de nuevo.',
    consentTitle: '¿Permitir que un agente de IA use {company}?',
    notVerified:
      'Un agente de IA que se identifica como «{client}» quiere usar {company}. Poneva no puede verificar el nombre.',
    notVerifiedOrigin:
      'Un agente de IA que se identifica como «{client}» en {origin} quiere usar {company}. Poneva no puede verificar el nombre.',
    dataLine:
      'Lo que lea se enviará a {vendor} con el plan propio de {person}, fuera del alojamiento de Poneva en la UE.',
    expiryDays:
      'El acceso dura {days} días. Puedes revocarlo en cualquier momento en Conexiones → Agentes de código.',
    expiryDay:
      'El acceso dura {days} día. Puedes revocarlo en cualquier momento en Conexiones → Agentes de código.',
    headingAsking: 'Solicita',
    headingRefused: 'Esta solicitud ha sido rechazada',
    headingConsented: 'Has permitido',
    headingRevoked: 'Este acceso ha sido revocado',
    headingStale: 'Esta solicitud está obsoleta',
    availAsked: 'Se permitirá',
    availAlready: 'Ya permitido',
    availNotAvailable: 'No disponible para ti',
    notAvailableNote: 'Tu rol en {company} no incluye esto, por lo que no se concederá.',
    notePartly:
      'Parte de lo que ha solicitado no está disponible para ti. Si lo permites, solo se concederá el acceso marcado como «Se permitirá».',
    noteBeyond:
      'Nada de lo que ha solicitado está disponible para ti, así que no hay nada que permitir. Pídeselo a un administrador de la empresa.',
    noteStale:
      'El acceso de tu empresa cambió después de que el agente hiciera la solicitud. Pídele al agente que vuelva a conectarse para revisar la solicitud actual.',
    noteRevoked: 'El agente ya no puede usar este acceso.',
    noteConsented: 'Puedes cambiar o revocar esto en cualquier momento desde Agentes de código.',
    refusalReadRequired:
      'Un agente no puede solicitar acceso a aplicaciones por sí solo. También necesita leer los conocimientos de la empresa. Pídele que vuelva a conectarse con ambos permisos.',
    refusalGeneral: 'La solicitud ha sido rechazada. Pídele al agente que vuelva a conectarse.',
    allow: 'Permitir',
    deny: 'Cancelar',
    working: 'Trabajando…',
    unconfirmed:
      'No hemos podido confirmar tu elección. Puede que se haya registrado o puede que no. Compruébalo de nuevo antes de volver a elegir.',
    check: 'Comprobar de nuevo',
    stillPending:
      'Poneva aún no ha resuelto esto. Puede que siga en proceso, así que no se puede decidir nada más hasta que se resuelva.',
    stillUnknown:
      'No hemos podido obtener una respuesta. No se puede decidir nada más hasta que esto se resuelva. Inténtalo de nuevo dentro de un momento.',
    requestClosed:
      'Esta solicitud está cerrada. Pídele al agente que vuelva a conectarse para iniciar una nueva.',
    recordedOther: 'Ya se había registrado otra elección: {choice}. Tu elección no se aplicó.',
    choiceAllow: 'permitido',
    choiceDeny: 'no permitido',
    staleOnSend:
      'Tu acceso cambió desde que se hizo esta solicitud. No se concedió ningún permiso. Pídele al agente que vuelva a conectarse.',
    refusedOnSend: 'Poneva ha rechazado esta solicitud. No se concedió ningún permiso.',
    noAccess: 'Sin acceso',
    lastSeenNever: 'Aún no se ha usado',
    lastSeenStale: 'No se ha visto recientemente · última vez {when}',
    lastSeen: 'Última vez {when}',
    stateActive: 'Conectado',
    stateRevoked: 'Revocado',
    stateExpired: 'Caducado',
    needsConsent: 'Solicita más acceso',
    review: 'Revisar solicitud',
    revoke: 'Revocar',
    revokeConfirm:
      '¿Revocar {client}? Dejará de funcionar de inmediato. Puedes volver a conectarlo más adelante.',
    revokeYes: 'Revocar',
    revokeNo: 'Mantener',
    revokeFailed: 'No se pudo revocar. Puede que siga conectado. Inténtalo de nuevo.',
    revokeDenied: 'No puedes revocar este agente.',
    revokeGone: 'Este agente ya no existe. La lista muestra el estado actual.',
    yourAgents: 'Tus agentes',
    companyAgents: 'Agentes de todos',
    companyAgentsHelp:
      'Como administrador de la empresa, puedes ver y revocar todos los agentes de programación.',
  },
  sv: {
    title: 'Kodagenter',
    intro:
      'De kodagenter du kör själv kan läsa företagets kunskap och arbeta med appar. Varje agent ber om precis den åtkomst den behöver, och ingenting utökas utan att du säger ja.',
    none: 'Inga kodagenter är anslutna ännu.',
    loading: 'Läser in kodagenter…',
    error: 'Vi kunde inte läsa in dina kodagenter. Försök igen.',
    consentTitle: 'Tillåt en AI-agent att använda {company}?',
    notVerified:
      'En AI-agent som kallar sig “{client}” vill använda {company}. Poneva kan inte verifiera namnet.',
    notVerifiedOrigin:
      'En AI-agent som kallar sig “{client}” på {origin} vill använda {company}. Poneva kan inte verifiera namnet.',
    dataLine:
      'Det den läser skickas till {vendor} under {person}s egen plan, utanför Ponevas EU-hosting.',
    expiryDays:
      'Åtkomsten gäller i {days} dagar. Du kan återkalla den när som helst under Anslutningar → Kodagenter.',
    expiryDay:
      'Åtkomsten gäller i {days} dag. Du kan återkalla den när som helst under Anslutningar → Kodagenter.',
    headingAsking: 'Den ber om att',
    headingRefused: 'Den här begäran avvisades',
    headingConsented: 'Du tillät',
    headingRevoked: 'Den här åtkomsten återkallades',
    headingStale: 'Den här begäran är inaktuell',
    availAsked: 'Kommer att tillåtas',
    availAlready: 'Redan tillåten',
    availNotAvailable: 'Inte tillgänglig för dig',
    notAvailableNote: 'Din roll hos {company} omfattar inte detta, så det beviljas inte.',
    notePartly:
      'En del av det agenten bad om är inte tillgängligt för dig. Om du tillåter beviljas bara den åtkomst som är markerad “Kommer att tillåtas”.',
    noteBeyond:
      'Inget av det agenten bad om är tillgängligt för dig, så det finns inget att tillåta. Fråga en företagsadministratör.',
    noteStale:
      'Din åtkomst i företaget ändrades efter att agenten bad om den. Be agenten ansluta igen så att du får granska den aktuella begäran.',
    noteRevoked: 'Agenten kan inte längre använda den här åtkomsten.',
    noteConsented: 'Du kan ändra eller återkalla detta när som helst under Kodagenter.',
    refusalReadRequired:
      'En agent kan inte be om enbart appåtkomst. Den behöver också kunna läsa företagets kunskap. Be agenten ansluta igen med båda.',
    refusalGeneral: 'Begäran avvisades. Be agenten ansluta igen.',
    allow: 'Tillåt',
    deny: 'Avbryt',
    working: 'Arbetar…',
    unconfirmed:
      'Vi kunde inte bekräfta ditt val. Det kan ha sparats eller inte. Kontrollera igen innan du väljer en gång till.',
    check: 'Kontrollera igen',
    stillPending:
      'Poneva har inte avgjort detta än. Det kan fortfarande pågå, så inget annat kan beslutas innan det är klart.',
    stillUnknown:
      'Vi fick inget svar. Inget annat kan beslutas innan detta är avgjort. Försök igen om en stund.',
    requestClosed: 'Den här begäran är stängd. Be agenten ansluta igen för att starta en ny.',
    recordedOther: 'Ett annat val hade redan sparats: {choice}. Ditt val användes inte.',
    choiceAllow: 'tillåten',
    choiceDeny: 'inte tillåten',
    staleOnSend:
      'Din åtkomst har ändrats sedan begäran gjordes. Ingenting beviljades. Be agenten ansluta igen.',
    refusedOnSend: 'Poneva avvisade begäran. Ingenting beviljades.',
    noAccess: 'Ingen åtkomst',
    lastSeenNever: 'Inte använd ännu',
    lastSeenStale: 'Inte sedd nyligen · senast sedd {when}',
    lastSeen: 'Senast sedd {when}',
    stateActive: 'Ansluten',
    stateRevoked: 'Återkallad',
    stateExpired: 'Utgången',
    needsConsent: 'Ber om mer åtkomst',
    review: 'Granska begäran',
    revoke: 'Återkalla',
    revokeConfirm: 'Återkalla {client}? Den slutar fungera direkt. Du kan ansluta den igen senare.',
    revokeYes: 'Återkalla',
    revokeNo: 'Behåll',
    revokeFailed: 'Vi kunde inte återkalla den. Den kan fortfarande vara ansluten. Försök igen.',
    revokeDenied: 'Du kan inte återkalla den här agenten.',
    revokeGone: 'Den här agenten finns inte längre. Listan visar det aktuella läget.',
    yourAgents: 'Dina agenter',
    companyAgents: 'Allas agenter',
    companyAgentsHelp: 'Som företagsadministratör kan du se och återkalla alla kodagenter.',
  },
};
export default copy;
